/**
 * screens/DMScreen.tsx — Direct-message conversation (route: DM, param id).
 *
 * MIGRATION (bare RN, P3 DMs / blueprint §2,§1): ported off apps/cryb-native
 * app/dm/[id].tsx. Swaps ONLY the Expo module layer; ALL chat correctness is
 * preserved VERBATIM: optimistic send + clientMessageId dedupe (LR2), typing,
 * delivered/read receipts, the offline send-queue flush, and the onReconnect
 * resync. The swaps:
 *   - expo-router → react-navigation (useLocalSearchParams → route.params;
 *     useRouter().push/back → navigation.navigate/goBack; the inline
 *     <Stack.Screen options> → navigation.setOptions via useLayoutEffect).
 *   - @expo/vector-icons → ../components/ui/Icon.
 *   - expo-image → react-native Image (contentFit→resizeMode, transition dropped).
 *   - expo-image-picker → react-native-image-picker (launchImageLibrary).
 *   - expo-audio (record) → services/audio recorder (m4a, lazy/graceful).
 *   - services/{api,socket,dmState,e2e,moderation} import unchanged.
 *   - A15 report/block (ReportBlockSheet) preserved.
 *
 * API / transport:
 *   GET  /direct-messages/conversations/:id/messages — history (oldest-first)
 *   GET  /direct-messages/conversations/:id          — conversation/other-user info
 *   POST /direct-messages/conversations/:id/messages — send a message
 *   socket: dm:message (via onDmMessage) — real-time incoming/echoed messages
 *
 * Key behavior:
 *   - Sends are optimistic: a temp-* bubble is added instantly, then either
 *     swapped for the server message or reverted (and text restored) on failure.
 *   - Real-time is additive on top of REST. Incoming socket messages are deduped
 *     by id; our own echoed message reconciles the matching temp-* bubble so a
 *     sent message never appears twice.
 *   - `is_mine` attribution relies on the current user id (meIdRef), resolved
 *     once best-effort; if unresolved, only bubble alignment for socket-arrived
 *     messages is affected.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Alert,
  Modal,
  Pressable,
  Animated,
  Linking,
  useWindowDimensions,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { launchImageLibrary } from 'react-native-image-picker';
import Video from 'react-native-video';
import { pick, keepLocalCopy, types as docTypes, isErrorWithCode, errorCodes } from '@react-native-documents/picker';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { runOnJS } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '../components/ui/Icon';
import type { RootStackParamList } from '../navigation/types';
import { colors, spacing, radius, fonts, typographyScale, shadowTokens, getLayout } from '../constants/theme';
import ParticleBackground from '../components/ui/ParticleBackground';
import Asset3D from '../components/ui/Asset3D';
import { GlassSurface, GlassPill } from '../components/glass';
import ContextMenu, {
  type ContextMenuAction,
  type ContextMenuAnchor,
} from '../components/glass/ContextMenu';
import { glassTokens, glassColorTokens } from '../components/glass/tokens';
import { api, getMe, uploadMedia, uploadAttachment } from '../services/api';
import { recorder } from '../services/audio';
import * as haptics from '../services/haptics';
import {
  connectSocket,
  onDmMessage,
  onDmRead,
  onDmTyping,
  onDmReaction,
  onReconnect,
  resyncSince,
  type DmMessagePayload,
  type DmReadPayload,
  type DmTypingPayload,
  type DmReactionPayload,
} from '../services/socket';
import { usePresence, formatLastSeen } from '../services/presence';
import {
  clearDmUnread,
  hydrateSendQueue,
  enqueueDmSend,
  dequeueDmSend,
  getQueuedForConversation,
} from '../services/dmState';
import {
  ensureKeypair,
  getSharedKey,
  refreshSharedKey,
  encrypt,
  decrypt,
  decryptResult,
  isEncrypted,
  encryptFileToUpload,
  decryptFileToLocal,
  mediaMarker,
  parseMediaMarker,
} from '../services/e2e';
import AudioMessageBubble from '../components/chat/AudioMessageBubble';
import EncryptedPlaceholder from '../components/chat/EncryptedPlaceholder';
import { DM_E2E_ENABLED } from '../lib/comingSoon';
import { tabularNums } from '../lib/format';
import ReportBlockSheet, { ReportTarget } from '../components/moderation/ReportBlockSheet';
import { isBlocked, onBlockedChange, isMuted, onMutedChange } from '../services/moderation';

type Nav = NativeStackNavigationProp<RootStackParamList, 'DM'>;
type DMRoute = RouteProp<RootStackParamList, 'DM'>;

// A media file attached to a message (image / audio / generic file).
interface Attachment {
  id: string;
  url: string;
  contentType: string;
  filename: string;
  size: number;
  width?: number;
  height?: number;
  duration?: number; // seconds (audio)
  // Free-form server field. For E2E media it carries our marker
  // ("e2emedia:v1:<realContentType>:<nonceB64>") so the receiver knows the blob at
  // `url` is encrypted, plus the real type + nonce needed to decrypt + render it.
  description?: string;
}

interface Message {
  id: string;
  content: string;
  sender_id: string;
  created_at: string;
  is_mine: boolean;
  attachments: Attachment[];
  // True while an optimistic attachment is still uploading.
  uploading?: boolean;
  // True while an optimistic text send is in flight (spinner in meta row).
  pending?: boolean;
  // True when a text send failed — the bubble shows a tap-to-retry affordance
  // (the typed text is preserved on the bubble, not silently dropped).
  failed?: boolean;
  // True when this message arrived end-to-end encrypted on the wire (the lock
  // glyph is shown even after we decrypt it for display — a security signal).
  encrypted?: boolean;
  // Stable client-supplied idempotency id (LR2). Set on optimistic sends and
  // echoed back by the server so the echo reconciles by THIS id (robust against
  // duplicate message text) rather than a fragile content match.
  clientMessageId?: string;
  // Swipe-to-reply: the message this one replies to (quoted block in the bubble).
  reply_to?: { id: string; content: string; senderName: string; is_mine: boolean } | null;
}

// The emoji set offered by the long-press reaction bar. Kept small + universal
// (WhatsApp/iMessage-style quick reactions).
const REACTION_EMOJIS = ['👍', '❤️', '😂', '🔥', '😮', '😢'] as const;

interface ConversationInfo {
  id: string;
  other_user: {
    id?: string;
    display_name: string;
    username: string;
    avatar_url?: string;
    is_online?: boolean;
  };
}

// Messages per history page (REST GET …/messages?page&limit). Older pages load
// when the user scrolls to the top (oldest) of the inverted list.
const HISTORY_PAGE = 30;

// Copy text to the clipboard WITHOUT adding a native dep. RN core still exports a
// (deprecated) Clipboard with setString(); accessed via a guarded require so the
// module's deprecation getter never fires at import time and a missing/renamed
// export just no-ops (returns false) instead of throwing. Same defensive shape as
// services/haptics.ts.
function copyToClipboard(text: string): boolean {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const Clipboard = require('react-native').Clipboard;
    if (Clipboard && typeof Clipboard.setString === 'function') {
      Clipboard.setString(text);
      return true;
    }
  } catch {
    /* no clipboard available — fall through */
  }
  return false;
}

// Wall-clock time of a message (e.g. "3:07 PM"); '' for missing/bad dates.
function formatTime(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const h = hours % 12 || 12;
    return `${h}:${minutes} ${ampm}`;
  } catch {
    return '';
  }
}

// "Today" / "Yesterday" / "Mon, Jun 3" date label for separators.
function formatDay(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  const same = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (same(d, today)) return 'Today';
  if (same(d, yest)) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

// True if two messages fall on different calendar days (drives separators).
function differentDay(a?: string, b?: string): boolean {
  if (!a || !b) return false;
  const da = new Date(a), db = new Date(b);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false;
  return da.toDateString() !== db.toDateString();
}

// Seconds → "m:ss" for the recording timer.
function formatClock(secs: number): string {
  const s = Number.isFinite(secs) && secs > 0 ? secs : 0;
  const m = Math.floor(s / 60);
  const r = (s % 60).toString().padStart(2, '0');
  return `${m}:${r}`;
}

// Renders an E2E-encrypted media attachment. The blob at `att.url` is ciphertext;
// this component downloads + decrypts it (via decryptFileToLocal, which caches by
// url) into a local plaintext file, then delegates back to the normal image/audio
// renderer through `renderResolved` using the REAL content type from the marker.
// While resolving it shows a small lock + spinner; if the key is missing or the
// decrypt fails it shows a tappable 🔒 "Encrypted media — tap to retry"
// placeholder that re-runs the download+decrypt (and picks up a refreshed shared
// key via the prop). Crash-safe: any failure lands on the placeholder, never an
// exception.
function EncryptedAttachment({
  att,
  marker,
  isMine,
  sharedKey,
  renderResolved,
}: {
  att: Attachment;
  marker: { contentType: string; nonce: string };
  isMine: boolean;
  sharedKey: Uint8Array | null;
  renderResolved: (resolved: Attachment) => React.ReactNode;
}) {
  const [localUri, setLocalUri] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  // Bumped by the tap-to-retry affordance; re-runs the download+decrypt effect.
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    let active = true;
    setLocalUri(null);
    setFailed(false);
    if (!sharedKey || !att.url) {
      setFailed(true);
      return;
    }
    decryptFileToLocal(att.url, marker.nonce, sharedKey, marker.contentType)
      .then((uri) => {
        if (!active) return;
        if (uri) setLocalUri(uri);
        else setFailed(true);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [att.url, marker.nonce, marker.contentType, sharedKey, retryTick]);

  if (localUri) {
    // Hand off to the normal renderer as the REAL media type, pointing at the
    // decrypted local file. Preserve dimensions/duration metadata for layout.
    return <>{renderResolved({ ...att, url: localUri, contentType: marker.contentType })}</>;
  }

  if (failed) {
    // Decrypt failed (or no key yet) → graceful tap-to-retry placeholder. The
    // retry re-runs the effect; a refreshed sharedKey prop also re-triggers it.
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={() => setRetryTick((t) => t + 1)}
        accessibilityRole="button"
        accessibilityLabel="Encrypted media — tap to retry decryption"
        style={[
          styles.encryptedPlaceholder,
          { backgroundColor: isMine ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)' },
        ]}
      >
        <Ionicons name="lock-closed" size={18} color={isMine ? colors.white : colors.muted} />
        <View>
          <Text style={[styles.encryptedPlaceholderText, { color: isMine ? colors.white : colors.muted }]}>
            Encrypted media
          </Text>
          <Text style={[styles.encryptedPlaceholderHint, { color: isMine ? 'rgba(255,255,255,0.7)' : colors.muted }]}>
            Tap to retry
          </Text>
        </View>
      </TouchableOpacity>
    );
  }

  // Resolving: small lock + spinner.
  return (
    <View
      style={[
        styles.encryptedPlaceholder,
        { backgroundColor: isMine ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)' },
      ]}
    >
      <Ionicons name="lock-closed" size={16} color={isMine ? colors.white : colors.muted} />
      <ActivityIndicator size="small" color={isMine ? colors.white : colors.primary} />
    </View>
  );
}

export default function DMScreen() {
  const navigation = useNavigation<Nav>();
  const route = useRoute<DMRoute>();
  const { id } = route.params;
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  const flatListRef = useRef<FlatList>(null);

  // --- State ---
  const [messages, setMessages] = useState<Message[]>([]); // newest-first (list is inverted)
  const [convo, setConvo] = useState<ConversationInfo | null>(null);
  // Trust & Safety (Apple 1.2): report/block sheet target + re-render on block.
  const [tsTarget, setTsTarget] = useState<ReportTarget | null>(null);
  const [, setBlockTick] = useState(0);
  useEffect(() => {
    const bump = () => setBlockTick((n) => n + 1);
    const unsubBlocked = onBlockedChange(bump);
    const unsubMuted = onMutedChange(bump);
    return () => { unsubBlocked(); unsubMuted(); };
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  // Swipe-to-reply / menu "Reply": the message being replied to (null = none).
  const [replyTarget, setReplyTarget] = useState<Message | null>(null);
  // --- History pagination (load older on scroll-to-top of the inverted list) ---
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const loadingOlderRef = useRef(false);
  const pageRef = useRef(1);
  // Which start-call request is in flight ('audio' | 'video'), or null. Doubles
  // as the double-tap guard AND drives the spinner on the matching header button.
  const [calling, setCalling] = useState<'audio' | 'video' | null>(null);
  // Which audio bubble (if any) is currently playing; enforces one-at-a-time.
  const [activeAudioId, setActiveAudioId] = useState<string | null>(null);
  // Full-screen image viewer source (null = closed).
  const [viewerUri, setViewerUri] = useState<string | null>(null);
  // --- Message context menu (long-press) ---
  // The long-pressed message + the on-screen frame of its bubble (measured via
  // measureInWindow) so the glass ContextMenu can grow FROM the bubble (skill §7).
  const [menuTarget, setMenuTarget] = useState<{ message: Message; anchor: ContextMenuAnchor } | null>(null);
  // --- Message reactions (long-press → emoji bar) ---
  // The long-pressed message whose floating emoji reaction bar is open (null =
  // closed), plus its measured on-screen frame so the bar can sit over the bubble.
  const [reactionTarget, setReactionTarget] = useState<{ message: Message; anchor: ContextMenuAnchor } | null>(null);
  // Reaction summary per message id: [{ emoji, count, mine }]. Server-backed —
  // seeded from message history (each message carries `reactions`), updated
  // optimistically on tap, POSTed to …/messages/:id/react, and kept in sync via
  // the `dm:reaction` socket event so both participants see the same state.
  type ReactionSummary = { emoji: string; count: number; mine: boolean };
  const [reactionsByMsg, setReactionsByMsg] = useState<Record<string, ReactionSummary[]>>({});
  // This viewer's active emoji on a message (at most one), derived from the map.
  const myReactionFor = useCallback(
    (messageId: string): string | undefined => reactionsByMsg[messageId]?.find((r) => r.mine)?.emoji,
    [reactionsByMsg]
  );
  // Absorb the `reactions` summaries carried by a batch of raw server messages.
  const absorbReactions = useCallback((rawList: any[]) => {
    if (!Array.isArray(rawList) || rawList.length === 0) return;
    setReactionsByMsg((prev) => {
      const next = { ...prev };
      for (const m of rawList) {
        if (m && m.id && Array.isArray(m.reactions)) {
          next[m.id] = m.reactions.map((r: any) => ({
            emoji: String(r.emoji),
            count: Number(r.count) || 0,
            mine: !!r.mine,
          }));
        }
      }
      return next;
    });
  }, []);
  // --- Lightweight ephemeral toast (self-contained; no global toast system) ---
  // Used to confirm Copy and to signal not-yet-wired actions (React / Reply /
  // Delete) as a gentle no-op rather than a blocking Alert. Fades a small glass
  // pill in/out above the composer (skill §12/§13: brief, non-vestibular status).
  const [toast, setToast] = useState<string | null>(null);
  const toastAnim = useRef(new Animated.Value(0)).current;
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    Animated.timing(toastAnim, { toValue: 1, duration: 160, useNativeDriver: true }).start();
    toastTimerRef.current = setTimeout(() => {
      Animated.timing(toastAnim, { toValue: 0, duration: 200, useNativeDriver: true }).start(
        ({ finished }) => { if (finished) setToast(null); },
      );
    }, 1600);
  }, [toastAnim]);
  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);
  // Current user id, used to mark incoming real-time messages as mine/theirs.
  const meIdRef = useRef<string | null>(null);

  // --- Typing indicator (the OTHER user is typing) ---
  const [peerTyping, setPeerTyping] = useState(false);
  // Last time WE sent a typing ping (throttle to ~once / 3s while typing).
  const lastTypingSentRef = useRef(0);
  // Auto-clears the peer's "typing…" line if no new ping arrives within ~4s.
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Read receipts ---
  // The newest message id WE'VE marked read (dedupe so we don't spam POST /read).
  const lastReadSentRef = useRef<string | null>(null);
  // The other user's last-read message id (drives "Read" under my latest bubble).
  const [peerLastReadId, setPeerLastReadId] = useState<string | null>(null);

  // --- Audio recording state (services/audio recorder) ---
  const [isRecording, setIsRecording] = useState(false);
  const [recordMillis, setRecordMillis] = useState(0);
  const recordSecs = Math.floor(recordMillis / 1000);
  // Guards against a stop()/upload that overlaps a new record press.
  const recordBusyRef = useRef(false);

  // --- End-to-end encryption (text only) ---
  // The shared X25519 secret with the OTHER user. null until derived (or if the
  // peer hasn't enabled E2E) — in which case we transparently fall back to
  // plaintext. A ref mirrors it so async/socket callbacks read the latest value
  // without being re-created. When set, BOTH text `content` AND media (image /
  // audio attachment bytes) are sealed; receivers decrypt media to a local file
  // before rendering (see EncryptedAttachment).
  const [sharedKey, setSharedKey] = useState<Uint8Array | null>(null);
  const sharedKeyRef = useRef<Uint8Array | null>(null);
  useEffect(() => {
    sharedKeyRef.current = sharedKey;
  }, [sharedKey]);

  // Normalize a server message (REST or socket shape) into our local Message.
  // Attachments come back as `MessageAttachment` (camelCase server relation);
  // be defensive about shape/nulls and prefer proxyUrl when present.
  const normalizeMessage = useCallback((m: any): Message => {
    const senderId = m?.userId ?? m?.sender_id ?? m?.User?.id ?? '';
    const rawAtt = Array.isArray(m?.MessageAttachment)
      ? m.MessageAttachment
      : Array.isArray(m?.attachments)
        ? m.attachments
        : [];
    const attachments: Attachment[] = rawAtt
      .filter((a: any) => a && (a.url || a.proxyUrl))
      .map((a: any, i: number) => ({
        id: a?.id ?? `att-${i}-${Date.now()}`,
        url: a?.proxyUrl || a?.url || '',
        contentType: a?.contentType || '',
        filename: a?.filename || 'file',
        size: typeof a?.size === 'number' ? a.size : 0,
        width: typeof a?.width === 'number' ? a.width : undefined,
        height: typeof a?.height === 'number' ? a.height : undefined,
        duration: typeof a?.duration === 'number' ? a.duration : undefined,
        // E2E media marker (free String). Preserved so the renderer can detect an
        // encrypted blob and decrypt it to a local uri before display.
        description: typeof a?.description === 'string' ? a.description : undefined,
      }));
    // E2E: server stores the ciphertext marker as `content`. Decrypt it for
    // display here so every downstream consumer (render + optimistic-echo
    // reconciliation, which matches on `content`) sees the plaintext. If the
    // peer's shared key isn't ready yet we keep the raw marker — render() shows a
    // locked placeholder and a later re-normalize (after the key resolves) will
    // reveal it. Plaintext (no marker) passes through untouched (backward compat).
    const rawContent = m?.content ?? '';
    const wasEncrypted = isEncrypted(rawContent);
    // E2E media markers also count as encrypted for the lock indicator.
    const hasEncryptedAttachment = attachments.some(
      (a) => typeof a.description === 'string' && a.description.startsWith('e2emedia:')
    );
    let content = rawContent;
    if (DM_E2E_ENABLED && wasEncrypted && sharedKeyRef.current) {
      content = decrypt(rawContent, sharedKeyRef.current) ?? rawContent;
    }
    const isMine = typeof m?.is_mine === 'boolean'
      ? m.is_mine
      : !!meIdRef.current && senderId === meIdRef.current;
    // Replied-to parent (server sends it as `Message` via the self-relation, or a
    // pre-shaped reply_to on optimistic/echo paths). Decrypt its preview too.
    const parent = m?.reply_to ?? m?.Message ?? null;
    let reply_to: Message['reply_to'] = null;
    if (parent && parent.id) {
      let pc = parent.content ?? '';
      if (DM_E2E_ENABLED && isEncrypted(pc) && sharedKeyRef.current) {
        pc = decrypt(pc, sharedKeyRef.current) ?? pc;
      } else if (isEncrypted(pc)) {
        pc = '';
      }
      const pSender = parent.senderName ?? parent.User?.displayName ?? parent.User?.username ?? '';
      const pMine = typeof parent.is_mine === 'boolean'
        ? parent.is_mine
        : !!meIdRef.current && (parent.userId ?? parent.sender_id) === meIdRef.current;
      reply_to = { id: parent.id, content: pc, senderName: pSender, is_mine: pMine };
    }
    return {
      id: m?.id ?? `srv-${Date.now()}`,
      content,
      sender_id: senderId,
      created_at: m?.createdAt ?? m?.created_at ?? new Date().toISOString(),
      is_mine: isMine,
      attachments,
      encrypted: wasEncrypted || hasEncryptedAttachment,
      clientMessageId: typeof m?.clientMessageId === 'string' ? m.clientMessageId : undefined,
      reply_to,
    };
  }, []);

  // Newest-first comparator: order by SERVER timestamp (created_at), with a
  // stable id tiebreaker so equal timestamps never thrash. Optimistic temp-*
  // bubbles (no server time yet) sort by their local created_at — once the echo
  // lands they take the server time and settle into place. Never trust socket
  // ARRIVAL order; always re-sort.
  const byServerTimeDesc = useCallback((a: Message, b: Message): number => {
    const ta = new Date(a.created_at).getTime();
    const tb = new Date(b.created_at).getTime();
    const va = Number.isNaN(ta) ? 0 : ta;
    const vb = Number.isNaN(tb) ? 0 : tb;
    if (va !== vb) return vb - va; // newest first
    return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
  }, []);

  // Insert/replace a message into the newest-first list, deduping by server id
  // AND clientMessageId, then keep it sorted by server timestamp.
  const mergeMessage = useCallback((prev: Message[], incoming: Message): Message[] => {
    // Already present by real id → ignore (a re-delivered echo).
    if (prev.some((m) => m.id === incoming.id && !m.id.startsWith('temp-'))) return prev;
    let replaced = false;
    const next = prev.map((m) => {
      // Reconcile an optimistic temp bubble by clientMessageId (preferred) or,
      // for legacy/no-cmid sends, by matching content on a pending temp.
      if (!replaced && m.id.startsWith('temp-')) {
        const cmidMatch =
          !!incoming.clientMessageId && m.clientMessageId === incoming.clientMessageId;
        const contentMatch =
          !incoming.clientMessageId &&
          incoming.is_mine &&
          ((!!incoming.content && m.content === incoming.content) ||
            (!incoming.content && incoming.attachments.length > 0 && !m.content && m.attachments.length > 0));
        if (cmidMatch || contentMatch) {
          replaced = true;
          return incoming;
        }
      }
      return m;
    });
    if (!replaced) next.unshift(incoming);
    return next.sort(byServerTimeDesc);
  }, [byServerTimeDesc]);

  const fetchMessages = useCallback(async () => {
    setError(null);
    const [msgRes, convoRes] = await Promise.all([
      api<{ messages?: any[] }>(`/direct-messages/conversations/${id}/messages?page=1&limit=${HISTORY_PAGE}`),
      api<ConversationInfo>(`/direct-messages/conversations/${id}`),
    ]);
    if (msgRes.success && msgRes.data) {
      // Backend wraps as { data: { messages: [...oldest-first], pagination } }.
      // The FlatList is inverted (newest-first) and raw messages use camelCase
      // (userId/createdAt/User), so normalize then order by SERVER timestamp
      // (newest-first) — never trust the REST array order. Guard empty-but-200.
      const raw = Array.isArray(msgRes.data?.messages) ? msgRes.data.messages : [];
      setMessages(raw.map(normalizeMessage).sort(byServerTimeDesc));
      absorbReactions(raw);
      pageRef.current = 1;
      // A full page implies older history may exist.
      setHasMore(raw.length >= HISTORY_PAGE);
    } else if (!msgRes.success) {
      setError(msgRes.error || 'Failed to load messages.');
    }
    if (convoRes.success && convoRes.data) {
      setConvo(convoRes.data);
    }
    setLoading(false);
  }, [id, normalizeMessage, byServerTimeDesc]);

  // Load an older page (inverted list → onEndReached fires at the TOP/oldest).
  // Pages are merged + de-duped + re-sorted; guards concurrent loads + no-more.
  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current || !hasMore || loading) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    const nextPage = pageRef.current + 1;
    try {
      const res = await api<{ messages?: any[] }>(
        `/direct-messages/conversations/${id}/messages?page=${nextPage}&limit=${HISTORY_PAGE}`,
      );
      const raw = res.success && Array.isArray(res.data?.messages) ? res.data!.messages : [];
      if (raw.length > 0) {
        const older = raw.map(normalizeMessage);
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const merged = [...prev, ...older.filter((m) => !seen.has(m.id))];
          return merged.sort(byServerTimeDesc);
        });
        absorbReactions(raw);
        pageRef.current = nextPage;
      }
      if (raw.length < HISTORY_PAGE) setHasMore(false);
    } catch {
      // keep hasMore so the user can retry by scrolling again
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [id, hasMore, loading, normalizeMessage, byServerTimeDesc]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // #82: re-sync on every RETURN to the conversation (not just first mount) so
  // reactions/messages you (or the peer) added show immediately, WhatsApp-style,
  // instead of appearing only after a manual refresh. Skips the very first focus
  // (the mount effect above already fetched).
  const didInitialFocus = useRef(false);
  useFocusEffect(
    useCallback(() => {
      if (!didInitialFocus.current) { didInitialFocus.current = true; return; }
      fetchMessages();
    }, [fetchMessages])
  );

  // Resolve current user id once (best-effort) so real-time messages can be
  // attributed correctly. Failure here is non-fatal — it just affects bubble
  // alignment for messages that arrive over the socket.
  useEffect(() => {
    let active = true;
    getMe()
      .then((res) => {
        const uid = (res as any)?.data?.id ?? (res as any)?.data?.user?.id;
        if (active && uid) meIdRef.current = uid;
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // E2E setup: ensure THIS device has a published keypair, then derive the
  // shared secret with the other user. Both are crash-safe and fire-and-forget —
  // a failure (peer has no key, network, crypto) never blocks the screen; we
  // simply stay on the plaintext path. Runs once the conversation info (and thus
  // the other user's username) is known.
  const otherUsername = convo?.other_user?.username;
  useEffect(() => {
    let active = true;
    // Publish our public key (idempotent; safe on every open).
    ensureKeypair().catch(() => {});
    if (!otherUsername) return;
    getSharedKey(otherUsername)
      .then((key) => {
        if (!active || !key) return;
        setSharedKey(key);
        sharedKeyRef.current = key;
        // Re-decrypt any messages already loaded (REST/socket) that arrived
        // before the key was ready, so locked placeholders resolve to plaintext.
        setMessages((prev) =>
          prev.map((m) =>
            isEncrypted(m.content)
              ? { ...m, content: decrypt(m.content, key) ?? m.content }
              : m
          )
        );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [otherUsername]);

  // --- E2E decrypt retry ("Couldn't decrypt — tap to retry") ---
  // Message ids with a manual decrypt-retry in flight (those bubbles show the
  // shimmer placeholder instead of the failed state while we re-key).
  const [retryingIds, setRetryingIds] = useState<Set<string>>(() => new Set());
  // Per-peer debounce for tap-to-retry: refreshSharedKey hits the network to
  // re-fetch the peer's public key, so rapid taps (impatient user hammering
  // several locked bubbles) would fire a burst of identical key re-fetches. Gate
  // to at most one refresh per peer per DECRYPT_RETRY_DEBOUNCE_MS; a retry inside
  // the window is a no-op (the single in-flight/just-finished refresh already
  // re-decrypts every locked bubble anyway).
  const DECRYPT_RETRY_DEBOUNCE_MS = 2000;
  const lastDecryptRetryRef = useRef<{ peer: string; at: number } | null>(null);
  // Tap-to-retry: force a FRESH key agreement with the peer (their key may have
  // rotated on reinstall/new device), then re-decrypt every still-locked message.
  // If decryption still fails the bubble simply returns to the failed state.
  const retryDecrypt = useCallback(
    async (msg: Message) => {
      const peer = otherUsername;
      if (!peer) return;
      // Debounce rapid taps per peer: skip if we refreshed this peer's key within
      // the window (the prior refresh's re-decrypt covers this bubble too).
      const now = Date.now();
      const last = lastDecryptRetryRef.current;
      if (last && last.peer === peer && now - last.at < DECRYPT_RETRY_DEBOUNCE_MS) {
        return;
      }
      lastDecryptRetryRef.current = { peer, at: now };
      setRetryingIds((prev) => {
        if (prev.has(msg.id)) return prev;
        const next = new Set(prev);
        next.add(msg.id);
        return next;
      });
      try {
        const key = await refreshSharedKey(peer);
        if (key) {
          setSharedKey(key);
          sharedKeyRef.current = key;
          setMessages((prev) =>
            prev.map((m) =>
              isEncrypted(m.content) ? { ...m, content: decrypt(m.content, key) ?? m.content } : m,
            ),
          );
        }
      } catch {
        // crash-safe: a failed refresh just leaves the bubble in the retry state
      } finally {
        setRetryingIds((prev) => {
          if (!prev.has(msg.id)) return prev;
          const next = new Set(prev);
          next.delete(msg.id);
          return next;
        });
      }
    },
    [otherUsername],
  );

  // --- Typing (send) ---
  // Fire a typing ping to the peer, THROTTLED to at most once / ~3s while the
  // user is actively typing. Fire-and-forget; never blocks input or throws.
  const TYPING_THROTTLE_MS = 3000;
  const notifyTyping = useCallback(() => {
    if (!id) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < TYPING_THROTTLE_MS) return;
    lastTypingSentRef.current = now;
    // Fire-and-forget; ignore the result.
    api(`/direct-messages/conversations/${id}/typing`, { method: 'POST' }).catch(() => {});
  }, [id]);

  // Input change: update the text AND (if non-empty) emit a throttled typing ping.
  const handleChangeText = useCallback(
    (text: string) => {
      setInputText(text);
      if (text.trim()) notifyTyping();
    },
    [notifyTyping]
  );

  // --- Read (send) ---
  // Mark the conversation read up to `messageId`, deduped so we only POST when
  // the newest message id actually changes. Skips temp-* (optimistic) ids and
  // our own messages. Fire-and-forget; the server emits dm:read to the peer.
  const markRead = useCallback(
    (messageId?: string | null) => {
      if (!id || !messageId) return;
      if (messageId.startsWith('temp-')) return;
      if (lastReadSentRef.current === messageId) return;
      lastReadSentRef.current = messageId;
      api(`/direct-messages/conversations/${id}/read`, {
        method: 'POST',
        body: JSON.stringify({ messageId }),
      }).catch(() => {
        // On failure, allow a later retry for this id.
        if (lastReadSentRef.current === messageId) lastReadSentRef.current = null;
      });
    },
    [id]
  );

  // Real-time layer (ADDITIVE): subscribe to dm:message for THIS conversation
  // and append incoming messages, deduped by id against the REST/optimistic copy.
  useEffect(() => {
    if (!id) return;
    connectSocket();

    const handler = (payload: DmMessagePayload) => {
      if (!payload || payload.conversationId !== id || !payload.message) return;
      const incoming = normalizeMessage(payload.message);
      // A real message from the peer means they've stopped typing — clear it.
      if (!incoming.is_mine) {
        setPeerTyping(false);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
        // Screen is mounted/focused, so mark their newest message read AND keep
        // the unread store at zero for this thread (we're actively viewing it).
        markRead(incoming.id);
        clearDmUnread(id);
      }
      // Merge: dedupe by server id + clientMessageId, reconcile our optimistic
      // temp bubble, and re-sort by SERVER timestamp (never arrival order).
      setMessages((prev) => mergeMessage(prev, incoming));
    };

    const unsubscribe = onDmMessage(handler);
    return () => {
      unsubscribe();
    };
  }, [id, normalizeMessage, markRead, mergeMessage]);

  // Real-time reactions (ADDITIVE): apply dm:reaction updates for THIS thread.
  // The server sends the full recomputed summary; its `mine` flags reflect the
  // ACTOR, so we recompute `mine` against MY id (preserving my active emoji when
  // it's someone else's reaction that changed the counts).
  useEffect(() => {
    if (!id) return;
    const handler = (payload: DmReactionPayload) => {
      if (!payload || payload.conversationId !== id || !payload.messageId) return;
      const summary = Array.isArray(payload.reactions) ? payload.reactions : [];
      setReactionsByMsg((prev) => {
        const myPrev = prev[payload.messageId]?.find((r) => r.mine)?.emoji;
        const mineEmoji = payload.userId === meIdRef.current
          ? (payload.action === 'added' ? payload.emoji : undefined)
          : myPrev;
        const next = summary.map((r) => ({
          emoji: String(r.emoji),
          count: Number(r.count) || 0,
          mine: !!mineEmoji && r.emoji === mineEmoji,
        }));
        return { ...prev, [payload.messageId]: next };
      });
    };
    const unsub = onDmReaction(handler);
    return () => unsub();
  }, [id]);

  // Read receipts + typing: subscribe to the peer's dm:read / dm:typing events
  // for THIS conversation. Additive + crash-safe; all handlers/timers are torn
  // down on unmount.
  useEffect(() => {
    if (!id) return;

    const readHandler = (payload: DmReadPayload) => {
      if (!payload || payload.conversationId !== id) return;
      // Ignore our own read events; we only care about the OTHER user reading.
      if (meIdRef.current && payload.readerId === meIdRef.current) return;
      if (payload.lastReadMessageId) setPeerLastReadId(payload.lastReadMessageId);
    };

    const typingHandler = (payload: DmTypingPayload) => {
      if (!payload || payload.conversationId !== id) return;
      // Only show "typing…" for the OTHER user.
      if (meIdRef.current && payload.userId === meIdRef.current) return;
      setPeerTyping(true);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        setPeerTyping(false);
        typingTimeoutRef.current = null;
      }, 4000);
    };

    const unsubRead = onDmRead(readHandler);
    const unsubTyping = onDmTyping(typingHandler);
    return () => {
      unsubRead();
      unsubTyping();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [id]);

  // Mark the conversation read once messages load (newest id) and whenever the
  // newest message changes to an incoming one. Deduped inside markRead.
  useEffect(() => {
    if (loading || messages.length === 0) return;
    const newest = messages[0];
    if (!newest || newest.is_mine) return;
    markRead(newest.id);
  }, [loading, messages, markRead]);

  // Opening this thread = it's read: clear its unread in the shared store so the
  // list badge + nav dot drop immediately (server-side mark-read confirms via the
  // markRead POSTs above). Also hydrate the persisted offline outbox once.
  useEffect(() => {
    if (!id) return;
    clearDmUnread(id);
    hydrateSendQueue().catch(() => {});
  }, [id]);

  // --- Reconnect resync (LR1 spine) ---
  // On every reconnect where the socket session was NOT recovered (gap > 2 min /
  // cold start / long background), pull everything missed via REST and merge any
  // messages for THIS conversation. Track the newest server time we've rendered as
  // the `since` cursor. Crash-safe: resync returns null on failure (REST history
  // remains the fallback).
  const minRenderedTsRef = useRef<string>(new Date().toISOString());
  useEffect(() => {
    // The newest message we've rendered is messages[0] (newest-first).
    const newestTs = messages[0]?.created_at;
    if (newestTs) minRenderedTsRef.current = newestTs;
  }, [messages]);

  useEffect(() => {
    if (!id) return;
    const unsub = onReconnect(({ recovered }) => {
      if (recovered) return; // session restored — missed events were replayed
      // Resync since the OLDEST gap boundary we trust (newest rendered ts).
      resyncSince(minRenderedTsRef.current, [])
        .then((result) => {
          if (!result) return;
          const mine = (Array.isArray(result.dmMessages) ? result.dmMessages : []).filter(
            (m: any) => m && m.conversationId === id,
          );
          if (mine.length === 0) return;
          setMessages((prev) => {
            let next = prev;
            for (const raw of mine) {
              next = mergeMessage(next, normalizeMessage(raw));
            }
            return next;
          });
          // We're viewing this thread, so anything resynced here is read.
          clearDmUnread(id);
        })
        .catch(() => {});
    });
    return () => unsub();
  }, [id, normalizeMessage, mergeMessage]);

  // --- Handlers ---
  // POST a text message and reconcile the optimistic bubble identified by tempId.
  // On success the bubble becomes the saved server message; on failure it is
  // marked `failed` (text preserved on the bubble) so the user can tap to retry
  // — we no longer silently drop the text back into the composer.
  const sendText = useCallback(async (text: string, tempId: string, clientMessageId: string, replyToId?: string) => {
    // E2E: if we have a shared key with the peer, send the SEALED ciphertext
    // marker as `content`; otherwise fall back to plaintext so sending is never
    // blocked.
    let wire = text;
    if (DM_E2E_ENABLED && sharedKeyRef.current) {
      const sealed = encrypt(text, sharedKeyRef.current);
      if (sealed) wire = sealed;
    }
    let res;
    try {
      res = await api<{ message: any }>(`/direct-messages/conversations/${id}/messages`, {
        method: 'POST',
        // clientMessageId rides along so the server echoes it back → the optimistic
        // bubble reconciles by a STABLE id, never a fragile content match.
        // replyToId (swipe-to-reply) — server validates it belongs to this convo.
        body: JSON.stringify({ content: wire, clientMessageId, ...(replyToId ? { replyToId } : {}) }),
      });
    } catch {
      res = { success: false, error: 'network' } as any;
    }
    if (res.success && res.data?.message) {
      const saved = { ...normalizeMessage(res.data.message), is_mine: true, clientMessageId };
      // Confirmed → drop it from the offline outbox if it was queued.
      dequeueDmSend(clientMessageId).catch(() => {});
      // mergeMessage reconciles the temp bubble by clientMessageId and re-sorts;
      // if the socket echo already landed, it dedupes by server id (no double).
      setMessages((prev) => mergeMessage(prev, saved));
    } else {
      // Offline / transient failure → park in the persisted outbox so it flushes
      // on reconnect, and mark the bubble failed (also tap-to-retry).
      enqueueDmSend({
        clientMessageId,
        conversationId: String(id),
        content: text,
        createdAt: new Date().toISOString(),
        // Capture the peer so the GLOBAL outbox flush (startDmOutbox) can re-seal
        // E2E on reconnect even when this screen is no longer mounted.
        peerUsername: otherUsername,
      }).catch(() => {});
      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...m, pending: false, failed: true } : m)),
      );
    }
  }, [id, normalizeMessage, mergeMessage, otherUsername]);

  // --- Offline send-queue flush ---
  // When the socket (re)connects, drain any persisted offline sends for THIS
  // conversation in order. Each carries its original clientMessageId, so a flush
  // that races a manual retry can't double-create. Failed flushes stay queued.
  // (Declared AFTER sendText so the dep array doesn't hit its temporal-dead-zone.)
  useEffect(() => {
    if (!id) return;
    const flush = async () => {
      const pending = getQueuedForConversation(String(id));
      if (pending.length === 0) return;
      // Peer-key-rotation safety: a queued send may have sat in the outbox across
      // a reconnect during which the peer rotated keys (reinstall / new device).
      // sendText seals against sharedKeyRef.current at send time, so refresh that
      // ref to the CURRENT shared key BEFORE draining — otherwise we'd re-encrypt
      // every queued message under the stale cached key and the peer could never
      // decrypt them. Crash-safe: on any failure we keep the existing key (or
      // plaintext), so a flush is never blocked.
      const peer = otherUsername;
      if (peer) {
        try {
          const fresh = await refreshSharedKey(peer);
          if (fresh) {
            sharedKeyRef.current = fresh;
            setSharedKey(fresh);
          }
        } catch {
          /* keep whatever key we had; sending is never blocked */
        }
      }
      for (const q of pending) {
        // Re-create an optimistic bubble if one isn't already on screen.
        const tempId = `temp-${q.clientMessageId}`;
        setMessages((prev) =>
          prev.some((m) => m.clientMessageId === q.clientMessageId)
            ? prev
            : mergeMessage(prev, {
                id: tempId,
                content: q.content,
                sender_id: 'me',
                created_at: q.createdAt,
                is_mine: true,
                attachments: [],
                pending: true,
                clientMessageId: q.clientMessageId,
              }),
        );
        await sendText(q.content, tempId, q.clientMessageId);
      }
    };
    const unsub = onReconnect(() => { flush().catch(() => {}); });
    // Also attempt an immediate flush on mount (covers the just-came-online case).
    flush().catch(() => {});
    return () => unsub();
  }, [id, sendText, mergeMessage, otherUsername]);

  // Send flow: clear the input, show an optimistic pending temp-* bubble, POST.
  async function handleSend() {
    const text = inputText.trim();
    if (!text || sending) return;
    setSending(true);
    setInputText('');
    const clientMessageId = `cmid-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const tempId = `temp-${clientMessageId}`;
    // Capture + clear the reply target up front so the preview closes instantly.
    const replyTo = replyTarget;
    setReplyTarget(null);
    // Optimistic add — always shows the user's typed PLAINTEXT, never ciphertext.
    const tempMsg: Message = {
      id: tempId,
      content: text,
      sender_id: 'me',
      created_at: new Date().toISOString(),
      is_mine: true,
      attachments: [],
      pending: true,
      clientMessageId,
      reply_to: replyTo
        ? { id: replyTo.id, content: replyTo.content, senderName: replyTo.is_mine ? 'You' : (convo?.other_user?.display_name || convo?.other_user?.username || 'Them'), is_mine: replyTo.is_mine }
        : null,
    };
    setMessages((prev) => [tempMsg, ...prev]);
    // Keep `sending` true through the network call so the guard/state stays honest
    // for the duration of the send (release in finally, never leaked on error).
    try {
      await sendText(text, tempId, clientMessageId, replyTo?.id);
    } finally {
      setSending(false);
    }
  }

  // Retry a previously-failed text message in place. Reuses the SAME
  // clientMessageId so a retry that races the offline-queue flush can't
  // double-create (server insert + dedupe are idempotent on this id).
  const retryText = useCallback(async (msg: Message) => {
    const cmid = msg.clientMessageId || `cmid-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setMessages((prev) =>
      prev.map((m) => (m.id === msg.id ? { ...m, failed: false, pending: true, clientMessageId: cmid } : m)),
    );
    await sendText(msg.content, msg.id, cmid, msg.reply_to?.id);
  }, [sendText]);

  // Shared attachment-send flow: show an optimistic bubble that renders the
  // local file immediately (marked `uploading`), upload to get a public URL,
  // then POST the attachment-only message and swap in the saved message. On any
  // failure the optimistic bubble is removed and the user is alerted.
  async function sendAttachmentMessage(
    localUri: string,
    // `contentType` here is the REAL media type (image/jpeg, audio/m4a, …).
    optimistic: Attachment,
    payload: { url: string; contentType: string; filename: string; size: number; width?: number; height?: number; duration?: number },
    // Plaintext uploader: media (image/video/audio) → /uploads/media; documents
    // → /uploads/attachment (the media endpoint rejects docs). E2E path is unaffected.
    uploader: (uri: string) => Promise<string | null> = uploadMedia
  ) {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; // unique (was Date.now() → same-ms collision)
    const tempMsg: Message = {
      id: tempId,
      content: '',
      sender_id: 'me',
      created_at: new Date().toISOString(),
      is_mine: true,
      // Optimistic bubble renders the LOCAL ORIGINAL immediately (no marker), so
      // the sender sees their own image/audio instantly even though the uploaded
      // copy is ciphertext.
      attachments: [optimistic],
      uploading: true,
    };
    setMessages((prev) => [tempMsg, ...prev]);

    // E2E media: when a shared key exists, encrypt the file bytes and upload the
    // OPAQUE ciphertext blob; the attachment then points at the ciphertext url,
    // is typed application/octet-stream, and carries the e2emedia marker in
    // `description`. On a null key OR any encryption/upload failure we fall back
    // to the EXISTING plaintext uploadMedia path — sending is never blocked.
    let wirePayload: typeof payload & { description?: string } = { ...payload };
    let uploadedUrl: string | null = null;

    const realType = payload.contentType;
    if (sharedKeyRef.current) {
      try {
        const enc = await encryptFileToUpload(localUri, realType, sharedKeyRef.current);
        if (enc) {
          uploadedUrl = enc.url;
          wirePayload = {
            ...payload,
            url: enc.url,
            contentType: 'application/octet-stream',
            description: mediaMarker(enc.contentType, enc.nonce),
          };
        }
      } catch {
        uploadedUrl = null;
      }
    }

    // Plaintext fallback (no key, or encryption failed): original behaviour.
    if (!uploadedUrl) {
      try {
        uploadedUrl = await uploader(localUri);
      } catch {
        uploadedUrl = null;
      }
      if (uploadedUrl) {
        wirePayload = { ...payload, url: uploadedUrl };
      }
    }

    if (!uploadedUrl) {
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      Alert.alert('Upload failed', 'Could not upload your attachment. Please try again.');
      return;
    }

    const res = await api<{ message: any }>(`/direct-messages/conversations/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ attachments: [wirePayload] }),
    });

    if (res.success && res.data?.message) {
      const saved = { ...normalizeMessage(res.data.message), is_mine: true };
      setMessages((prev) => prev.map((m) => (m.id === tempId ? saved : m)));
    } else {
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      Alert.alert('Send failed', res.error || 'Could not send your attachment.');
    }
  }

  // IMAGE: pick from library → optimistic local bubble → upload → send.
  // react-native-image-picker handles permissions internally (the OS prompt fires
  // on launch); a denied/cancelled pick just returns didCancel/errorCode.
  async function handlePickImage() {
    if (sending) return;
    try {
      const result = await launchImageLibrary({ mediaType: 'mixed', quality: 0.8, selectionLimit: 1 });
      if (result.didCancel || result.errorCode || !result.assets?.[0]) {
        if (result.errorCode === 'permission') {
          Alert.alert('Permission needed', 'Photo library access is required to share media.');
        }
        return;
      }
      const asset = result.assets[0];
      const uri = asset.uri;
      if (!uri) return;
      const width = typeof asset.width === 'number' ? asset.width : undefined;
      const height = typeof asset.height === 'number' ? asset.height : undefined;
      const isVideo = (asset.type || '').startsWith('video/');
      const filename = asset.fileName || uri.split('/').pop() || `${isVideo ? 'video' : 'image'}-${Date.now()}.${isVideo ? 'mp4' : 'jpg'}`;

      await sendAttachmentMessage(
        uri,
        {
          id: `local-${Date.now()}`,
          url: uri, // local uri renders instantly while uploading
          contentType: asset.type || 'image/jpeg',
          filename,
          size: typeof asset.fileSize === 'number' ? asset.fileSize : 0,
          width,
          height,
        },
        { url: '', contentType: asset.type || 'image/jpeg', filename, size: typeof asset.fileSize === 'number' ? asset.fileSize : 0, width, height }
      );
    } catch {
      Alert.alert('Error', 'Could not open the image picker.');
    }
  }

  // DOCUMENT: pick a file (pdf/office/text/zip) → copy to a readable path →
  // upload via /uploads/attachment (docs allowlist) → send as an attachment.
  async function handlePickDocument() {
    if (sending) return;
    try {
      const [file] = await pick({
        type: [docTypes.pdf, docTypes.docx, docTypes.doc, docTypes.xlsx, docTypes.xls, docTypes.ppt, docTypes.pptx, docTypes.plainText, docTypes.csv, docTypes.zip],
      });
      if (!file?.uri) return;
      const name = file.name || file.uri.split('/').pop() || `document-${Date.now()}`;
      const ct = file.type || 'application/octet-stream';
      const size = typeof file.size === 'number' ? file.size : 0;
      // iOS hands back a security-scoped URL; copy to caches so fetch/FormData can read it.
      let uploadUri = file.uri;
      try {
        const [copy] = await keepLocalCopy({ files: [{ uri: file.uri, fileName: name }], destination: 'cachesDirectory' });
        if (copy?.status === 'success' && copy.localUri) uploadUri = copy.localUri;
      } catch { /* fall back to the original uri */ }
      await sendAttachmentMessage(
        uploadUri,
        { id: `local-${Date.now()}`, url: uploadUri, contentType: ct, filename: name, size },
        { url: '', contentType: ct, filename: name, size },
        (uri) => uploadAttachment(uri).then((r) => r?.url ?? null),
      );
    } catch (e) {
      if (isErrorWithCode(e) && e.code === errorCodes.OPERATION_CANCELED) return;
      Alert.alert('Error', 'Could not attach the document.');
    }
  }

  // AUDIO: start recording (mic press). services/audio recorder handles the mic
  // permission OS prompt; a denied/failed start just returns false (graceful).
  async function handleStartRecording() {
    if (isRecording || recordBusyRef.current) return;
    try {
      const ok = await recorder.start();
      if (!ok) {
        Alert.alert('Recording error', 'Could not start recording. Microphone access may be required.');
        return;
      }
      setIsRecording(true);
      setRecordMillis(0);
      recorder.subscribe(({ durationMillis }) => setRecordMillis(durationMillis));
    } catch {
      Alert.alert('Recording error', 'Could not start recording.');
    }
  }

  // AUDIO: stop recording → upload → send. `cancel` discards without sending.
  async function handleStopRecording(cancel = false) {
    if (recordBusyRef.current) return;
    recordBusyRef.current = true;
    const seconds = Math.floor(recordMillis / 1000);
    let uri: string | null = null;
    try {
      uri = await recorder.stop();
    } catch {
      uri = null;
    }
    setIsRecording(false);
    setRecordMillis(0);
    recordBusyRef.current = false;

    if (cancel || !uri || seconds < 1) {
      if (!cancel && uri && seconds < 1) {
        Alert.alert('Too short', 'Tap the mic and speak a little longer for a voice message.');
      }
      return;
    }

    const filename = uri.split('/').pop() || `voice-${Date.now()}.m4a`;
    await sendAttachmentMessage(
      uri,
      {
        id: `local-${Date.now()}`,
        url: uri,
        contentType: 'audio/m4a',
        filename,
        size: 0,
        duration: seconds,
      },
      { url: '', contentType: 'audio/m4a', filename, size: 0, duration: seconds }
    );
  }

  // Start a 1:1 audio OR video call with the OTHER participant of this thread.
  // Uses the peer's userId from the conversation header info. POSTs
  // /voice/video/call (which also rings the peer via Socket.IO), then opens the
  // full-screen call screen with the returned LiveKit token/url.
  //
  // Media kind: the backend has NO separate `media` body field — it derives
  // media = videoEnabled ? 'video' : 'audio' and carries it to the callee's ring
  // (socket `incomingVideoCall`.callType + push data.media). So audio calls send
  // videoEnabled:false, and the SAME kind rides into the VoiceCall params so the
  // caller's screen matches what the callee was rung with. Double-tap guarded;
  // crash-safe.
  async function handleCall(media: 'audio' | 'video') {
    if (calling) return;
    const peer = convo?.other_user;
    const peerId = peer?.id;
    if (!peerId) {
      Alert.alert('Unavailable', 'Could not start a call in this conversation.');
      return;
    }
    setCalling(media);
    try {
      const res = await api<any>('/voice/video/call', {
        method: 'POST',
        body: JSON.stringify({
          targetUserId: peerId,
          callType: 'direct',
          videoEnabled: media === 'video',
          audioEnabled: true,
        }),
      });
      const token = res.data?.token;
      const url = res.data?.liveKitUrl || res.data?.url;
      if (res.success && token && url) {
        navigation.navigate('VoiceCall', {
          callId: res.data?.callId ?? '',
          token,
          url,
          media,
          roomName: res.data?.roomName ?? '',
          peerName: peer?.display_name || peer?.username || 'User',
          peerAvatar: peer?.avatar_url || '',
          isCaller: true,
        });
      } else if ((res as any).code === 'USER_BUSY' || (res as any).data?.busy || /busy/i.test(res.error || '')) {
        // Callee is already on another call (server 409). LR8 BUSY signal.
        Alert.alert('Busy', `${peer?.display_name || peer?.username || 'They'} is on another call right now.`);
      } else {
        Alert.alert('Call failed', res.error || 'Could not start the call. Please try again.');
      }
    } catch {
      Alert.alert('Call failed', 'Could not start the call. Please try again.');
    } finally {
      setCalling(null);
    }
  }

  // Long-press → measure the bubble's on-screen frame (measureInWindow) then open
  // the glass ContextMenu anchored to it (skill §7: grows FROM the trigger). A
  // light haptic marks the "grab" (skill §13). Measurement is async + crash-safe;
  // if the node can't be measured we still open the menu (falls back to centered).
  const openMessageMenu = useCallback((message: Message, node: any) => {
    haptics.impact('light');
    const open = (anchor: ContextMenuAnchor) => setMenuTarget({ message, anchor });
    try {
      if (node && typeof node.measureInWindow === 'function') {
        node.measureInWindow((x: number, y: number, w: number, h: number) => {
          if ([x, y, w, h].every((n) => typeof n === 'number' && Number.isFinite(n))) {
            open({ x, y, width: w, height: h });
          } else {
            open({ x: 0, y: 0, width: 0, height: 0 });
          }
        });
      } else {
        open({ x: 0, y: 0, width: 0, height: 0 });
      }
    } catch {
      open({ x: 0, y: 0, width: 0, height: 0 });
    }
  }, []);

  // Long-press → open the floating emoji reaction bar anchored to the bubble
  // (same measure-then-anchor pattern as openMessageMenu). Light haptic marks the
  // grab. Crash-safe: falls back to a centered bar if the node can't be measured.
  const openReactionBar = useCallback((message: Message, node: any) => {
    haptics.impact('light');
    const open = (anchor: ContextMenuAnchor) => setReactionTarget({ message, anchor });
    try {
      if (node && typeof node.measureInWindow === 'function') {
        node.measureInWindow((x: number, y: number, w: number, h: number) => {
          if ([x, y, w, h].every((n) => typeof n === 'number' && Number.isFinite(n))) {
            open({ x, y, width: w, height: h });
          } else {
            open({ x: 0, y: 0, width: 0, height: 0 });
          }
        });
      } else {
        open({ x: 0, y: 0, width: 0, height: 0 });
      }
    } catch {
      open({ x: 0, y: 0, width: 0, height: 0 });
    }
  }, []);

  // Toggle a reaction on a message. OPTIMISTIC then persisted to the server
  // (POST …/messages/:id/react); the chip updates instantly and the `dm:reaction`
  // socket broadcast keeps the other participant in sync. One active reaction per
  // user per message: same emoji again clears it, a different emoji replaces it.
  const toggleReaction = useCallback((messageId: string, emoji: string) => {
    haptics.impact('light');
    setReactionTarget(null);

    // Optimistic local update mirroring the server's replace/toggle semantics.
    setReactionsByMsg((prev) => {
      const list = (prev[messageId] || []).map((r) => ({ ...r }));
      const mineIdx = list.findIndex((r) => r.mine);
      const removingSame = mineIdx >= 0 && list[mineIdx].emoji === emoji;
      // Drop my previous reaction (if any).
      if (mineIdx >= 0) {
        list[mineIdx].count = Math.max(0, list[mineIdx].count - 1);
        list[mineIdx].mine = false;
      }
      // Add the new one unless we were toggling the same emoji off.
      if (!removingSame) {
        const idx = list.findIndex((r) => r.emoji === emoji);
        if (idx >= 0) { list[idx].count += 1; list[idx].mine = true; }
        else list.push({ emoji, count: 1, mine: true });
      }
      return { ...prev, [messageId]: list.filter((r) => r.count > 0) };
    });

    // Persist + reconcile with server truth (authoritative counts + `mine`).
    (async () => {
      try {
        const res = await api<{ reactions?: ReactionSummary[] }>(
          `/direct-messages/conversations/${id}/messages/${messageId}/react`,
          { method: 'POST', body: JSON.stringify({ emoji }) }
        );
        if (res.success && Array.isArray(res.data?.reactions)) {
          setReactionsByMsg((prev) => ({
            ...prev,
            [messageId]: res.data!.reactions!.map((r: any) => ({
              emoji: String(r.emoji), count: Number(r.count) || 0, mine: !!r.mine,
            })),
          }));
        }
      } catch {
        // Network failure: keep the optimistic state; a later socket event or a
        // history refetch reconciles it. Never crash the thread over a reaction.
      }
    })();
  }, [id, absorbReactions]);

  // Enter reply mode for a message (from swipe or the context menu). Closes any
  // open menus and shows the reply preview above the composer.
  const beginReply = useCallback((message: Message) => {
    setMenuTarget(null);
    setReactionTarget(null);
    setReplyTarget(message);
  }, []);

  // Build the context-menu rows for the long-pressed message. Only actions the
  // screen can honor are wired; the rest are gated as gentle no-op toasts (never
  // omitted silently) so the menu stays predictable (skill §16.2 agency):
  //   • Copy   — client-side (RN Clipboard), only when there's decrypted text.
  //   • React  — opens the emoji reaction bar (server-backed, persisted).
  //   • Reply  — no reply/threading in this screen → toast "coming soon".
  //   • Report — WIRED: opens the existing ReportBlockSheet (their messages only),
  //              preserving the previous long-press report/block affordance.
  //   • Delete — destructive; no delete endpoint → toast "coming soon" (kept last).
  const buildMenuActions = useCallback(
    (message: Message): ContextMenuAction[] => {
      const locked = isEncrypted(message.content);
      const canCopy = !!message.content && !locked;
      const actions: ContextMenuAction[] = [];
      if (canCopy) {
        actions.push({
          key: 'copy',
          label: 'Copy',
          icon: 'copy-outline',
          onPress: () => showToast(copyToClipboard(message.content) ? 'Copied' : 'Nothing to copy'),
        });
      }
      actions.push({
        key: 'react',
        label: 'React',
        icon: 'happy-outline',
        onPress: () => setReactionTarget({ message, anchor: { x: 0, y: 0, width: 0, height: 0 } }),
      });
      actions.push({
        key: 'reply',
        label: 'Reply',
        icon: 'arrow-undo-outline',
        onPress: () => beginReply(message),
      });
      if (!message.is_mine) {
        actions.push({
          key: 'report',
          label: 'Report',
          icon: 'flag-outline',
          onPress: () => {
            const ou = convo?.other_user;
            setTsTarget({
              type: 'message',
              authorId: ou?.id || message.sender_id,
              authorUsername: ou?.username,
              authorLabel: ou ? `@${ou.username}` : 'this user',
              text: locked ? undefined : message.content,
            });
          },
        });
      }
      if (message.is_mine) {
        actions.push({
          key: 'delete',
          label: 'Delete',
          icon: 'trash-outline',
          destructive: true,
          onPress: () => showToast('Delete coming soon'),
        });
      }
      return actions;
    },
    [convo?.other_user, showToast],
  );

  // --- Render ---
  // Render a single attachment by content type: image → tappable image bubble,
  // audio → play/pause bubble, anything else → a generic file chip.
  function renderAttachment(att: Attachment, isMine: boolean, uploading: boolean) {
    // E2E media: if this attachment's description is one of our markers, the blob
    // at `att.url` is ciphertext. While still optimistically uploading we already
    // render the LOCAL original (att.url is a file:// uri, not the ciphertext), so
    // only treat it as encrypted once it's the saved server copy.
    const marker = !uploading ? parseMediaMarker(att.description) : null;
    if (marker) {
      return (
        <EncryptedAttachment
          key={att.id}
          att={att}
          marker={marker}
          isMine={isMine}
          sharedKey={sharedKey}
          renderResolved={(resolved) => renderAttachment(resolved, isMine, false)}
        />
      );
    }
    const ct = att.contentType || '';
    if (ct.startsWith('image/')) {
      // Cap width ~240 and preserve aspect ratio from the asset dimensions.
      const maxW = 240;
      const ratio = att.width && att.height ? att.height / att.width : 0.75;
      const w = Math.min(maxW, att.width || maxW);
      const h = Math.round(w * ratio);
      return (
        <TouchableOpacity
          key={att.id}
          activeOpacity={0.9}
          disabled={uploading}
          onPress={() => att.url && setViewerUri(att.url)}
          accessibilityRole="imagebutton"
          accessibilityLabel="Open image"
        >
          <CachedImage
            source={{ uri: att.url }}
            style={[styles.imageAttachment, { width: w, height: h }]}
            resizeMode="cover"
          />
          {uploading && (
            <View style={styles.uploadOverlay}>
              <ActivityIndicator size="small" color={colors.white} />
              <Text style={styles.uploadOverlayText}>Uploading…</Text>
            </View>
          )}
        </TouchableOpacity>
      );
    }
    if (ct.startsWith('video/')) {
      const maxW = 240;
      const ratio = att.width && att.height ? att.height / att.width : 0.66;
      const w = Math.min(maxW, att.width || maxW);
      const h = Math.round(w * ratio);
      return (
        <View key={att.id} style={[styles.imageAttachment, { width: w, height: h, overflow: 'hidden', backgroundColor: '#000' }]}>
          <Video
            source={{ uri: att.url }}
            style={{ width: w, height: h }}
            resizeMode="cover"
            paused
            controls
            repeat={false}
          />
          {uploading && (
            <View style={styles.uploadOverlay}>
              <ActivityIndicator size="small" color={colors.white} />
              <Text style={styles.uploadOverlayText}>Uploading…</Text>
            </View>
          )}
        </View>
      );
    }
    if (ct.startsWith('audio/')) {
      return (
        <View key={att.id} style={styles.audioWrap}>
          <AudioMessageBubble
            uri={att.url}
            durationSeconds={att.duration}
            isMine={isMine}
            bubbleId={att.id}
            activeId={activeAudioId}
            onActivate={setActiveAudioId}
          />
          {uploading && (
            <Text style={[styles.audioUploading, { color: isMine ? 'rgba(255,255,255,0.8)' : colors.muted }]}>
              Uploading…
            </Text>
          )}
        </View>
      );
    }
    // Generic file/document chip — tap to open (download/preview via the OS).
    return (
      <TouchableOpacity
        key={att.id}
        style={styles.fileChip}
        activeOpacity={0.7}
        disabled={uploading || !att.url}
        onPress={() => att.url && Linking.openURL(att.url).catch(() => Alert.alert('Cannot open', 'This file could not be opened.'))}
        accessibilityRole="button"
        accessibilityLabel={`Open ${att.filename || 'attachment'}`}
      >
        <Ionicons name="document-outline" size={18} color={isMine ? colors.white : colors.primary} />
        <Text
          numberOfLines={1}
          style={[styles.fileChipText, { color: isMine ? colors.white : colors.ink }]}
        >
          {att.filename || 'Attachment'}
        </Text>
        {uploading && <ActivityIndicator size="small" color={isMine ? colors.white : colors.primary} />}
      </TouchableOpacity>
    );
  }

  // A single chat bubble, aligned/colored by ownership (`is_mine`). A message
  // can be text-only, attachment-only, or both. WhatsApp-style grouping (skill
  // §16.4/§16.6): `sameSenderAsNewer` (the bubble directly BELOW this one, i.e.
  // index-1 in the inverted list, is from the same sender) tightens spacing and
  // drops the asymmetric tail + timestamp so a run reads as one grouped block;
  // only the LAST bubble of a run keeps its tail + meta row.
  function renderMessage(
    { item }: { item: Message },
    grouping: { sameSenderAsNewer: boolean } = { sameSenderAsNewer: false },
  ) {
    const isMine = item.is_mine;
    const { sameSenderAsNewer } = grouping;
    // Last-of-run = nothing same-sender directly below → show tail + meta.
    const isLastOfGroup = !sameSenderAsNewer;
    const hasAttachments = item.attachments.length > 0;
    // E2E display state machine — a marker NEVER renders as raw ciphertext:
    //   1. DECRYPTING: content is still a marker and the shared key hasn't
    //      resolved yet (or a manual retry is in flight) → shimmering 🔒
    //      "Encrypted message" placeholder.
    //   2. FAILED: content is still a marker WITH a key present → decryption
    //      failed (peer rotated keys / tampered) → "Couldn't decrypt — tap to
    //      retry"; tapping re-derives the shared key and re-decrypts.
    //   3. PLAINTEXT: normal text render.
    const locked = isEncrypted(item.content);
    // With E2E gated OFF, decryption can NEVER succeed (no key is derived and new
    // sends are plaintext), so a locked bubble is a pre-gate encrypted message
    // that will forever be unreadable on this client. Render it as a neutral,
    // non-actionable "🔒 Message unavailable" — NOT a tap-to-retry that can't work.
    const unreadableGated = locked && !DM_E2E_ENABLED;
    // Reason-driven E2E display (E2E ON only): probe the still-locked marker with
    // the current key to distinguish "waiting for key" (spinner) from "auth failed
    // / key rotated" (tap-to-retry). A manual retry in flight always shows spinner.
    const probe = locked && DM_E2E_ENABLED ? decryptResult(item.content, sharedKey) : null;
    const decrypting =
      locked && DM_E2E_ENABLED && (retryingIds.has(item.id) || probe?.success === false && probe.reason === 'no-key');
    const decryptFailed = locked && DM_E2E_ENABLED && !decrypting;
    const hasText = !!item.content;
    // Long-press ANY real bubble → floating glass ContextMenu (Copy / React /
    // Reply / Report / Delete), anchored to this bubble's measured frame. The ref
    // captures the pressed bubble node so openMessageMenu can measureInWindow it.
    // Skipped only for pending/failed optimistic sends (no server message yet to
    // act on). The previous report/block long-press now lives as the menu's
    // "Report" row (their messages), so that affordance is preserved.
    const bubbleRef = React.createRef<View>();
    const menuable = !item.pending && !item.failed;
    // Long-press opens the emoji REACTION bar (primary gesture). The full context
    // menu (Copy / Reply / Report / Delete) is reachable from the bar's trailing
    // "⋯" affordance (see ReactionBar). Unreadable gated bubbles can still be
    // long-pressed to report/react.
    const onLongPress = menuable
      ? () => openReactionBar(item, bubbleRef.current)
      : undefined;
    const itemReactions = reactionsByMsg[item.id] || [];
    // Tap a failed-decrypt bubble → refresh the peer key + retry (state 2 only).
    const onPress = decryptFailed ? () => retryDecrypt(item) : undefined;
    return (
      <View
        style={[
          styles.messageBubbleWrap,
          isMine ? styles.myMessageWrap : styles.theirMessageWrap,
          // Tighter intra-group spacing when another same-sender bubble sits
          // directly below (skill §16.6) — one run, not stacked islands.
          !isLastOfGroup && styles.groupedWrap,
        ]}
      >
        <Pressable
          ref={bubbleRef}
          onPress={onPress}
          onLongPress={onLongPress}
          delayLongPress={350}
          accessibilityRole={onPress || onLongPress ? 'button' : undefined}
          accessibilityLabel={
            decryptFailed
              ? 'Encrypted message could not be decrypted — tap to retry'
              : onLongPress
                ? 'Message — long-press to react or open options'
                : undefined
          }
          style={[
            styles.messageBubble,
            isMine ? styles.myBubble : styles.theirBubble,
            // Tail (asymmetric small corner) only on the LAST bubble of a run;
            // grouped (non-last) bubbles use full radius on every corner.
            !isLastOfGroup && (isMine ? styles.myBubbleGrouped : styles.theirBubbleGrouped),
          ]}
        >
          {/* Quoted reply block (swipe-to-reply). Sits at the top of the bubble. */}
          {item.reply_to && (
            <View style={[styles.quoteBlock, isMine ? styles.quoteBlockMine : styles.quoteBlockTheirs]}>
              <View style={[styles.quoteBar, isMine ? styles.quoteBarMine : styles.quoteBarTheirs]} />
              <View style={{ flex: 1 }}>
                <Text style={[styles.quoteName, isMine && styles.quoteNameMine]} numberOfLines={1}>
                  {item.reply_to.is_mine ? 'You' : (item.reply_to.senderName || 'Them')}
                </Text>
                <Text style={[styles.quoteText, isMine && styles.quoteTextMine]} numberOfLines={1}>
                  {item.reply_to.content?.trim() || 'Attachment'}
                </Text>
              </View>
            </View>
          )}
          {hasAttachments && (
            <View style={hasText ? styles.attachmentBlock : undefined}>
              {item.attachments.map((att) =>
                renderAttachment(att, isMine, !!item.uploading)
              )}
            </View>
          )}
          {locked ? (
            unreadableGated ? (
              // E2E is OFF → this pre-gate ciphertext can never be decrypted here.
              // Neutral, NON-actionable placeholder (no tap-to-retry affordance).
              <View style={styles.decryptFailRow}>
                <Ionicons
                  name="lock-closed"
                  size={14}
                  color={isMine ? 'rgba(255,255,255,0.85)' : colors.muted}
                />
                <Text
                  style={[
                    styles.messageText,
                    isMine ? styles.myMessageText : styles.theirMessageText,
                    { fontStyle: 'italic', opacity: 0.85 },
                  ]}
                >
                  Message unavailable
                </Text>
              </View>
            ) : decrypting ? (
              <EncryptedPlaceholder isMine={isMine} />
            ) : (
              <View style={styles.decryptFailRow}>
                <Ionicons
                  name="lock-closed"
                  size={14}
                  color={isMine ? 'rgba(255,255,255,0.85)' : colors.muted}
                />
                <View>
                  <Text
                    style={[
                      styles.messageText,
                      isMine ? styles.myMessageText : styles.theirMessageText,
                    ]}
                  >
                    Couldn’t decrypt
                  </Text>
                  <Text
                    style={[
                      styles.decryptRetryHint,
                      { color: isMine ? 'rgba(255,255,255,0.7)' : colors.muted },
                    ]}
                  >
                    Tap to retry
                  </Text>
                </View>
              </View>
            )
          ) : hasText ? (
            <Text
              // selectable → long-press to copy; dataDetectorType="link" (iOS)
              // autolinks URLs a peer sends so they're tappable, not dead text.
              selectable
              dataDetectorType="link"
              style={[
                styles.messageText,
                isMine ? styles.myMessageText : styles.theirMessageText,
              ]}
            >
              {item.content}
            </Text>
          ) : null}
          {/* Meta row (lock + time/spinner) only on the LAST bubble of a run —
              keeps grouped runs clean like WhatsApp (skill §16.6). A still-pending
              send always shows its spinner even mid-group so state stays visible. */}
          {(isLastOfGroup || item.pending) && (
            <View style={styles.messageMetaRow}>
              {item.encrypted && !locked && (
                <Ionicons
                  name="lock-closed"
                  size={10}
                  color={isMine ? 'rgba(255,255,255,0.7)' : colors.muted}
                  style={styles.messageLock}
                  accessibilityLabel="End-to-end encrypted"
                />
              )}
              {item.pending ? (
                <ActivityIndicator
                  size="small"
                  color={isMine ? 'rgba(255,255,255,0.7)' : colors.muted}
                  style={{ transform: [{ scale: 0.6 }] }}
                />
              ) : (
                <Text
                  style={[
                    styles.messageTime,
                    isMine ? styles.myMessageTime : styles.theirMessageTime,
                  ]}
                >
                  {formatTime(item.created_at)}
                </Text>
              )}
              {/* WhatsApp-style delivery ticks on MY messages: ✓✓ grey = delivered,
                  ✓✓ blue = read (the peer's read marker sits on this bubble). A
                  failed send shows a red alert instead. */}
              {isMine && !item.pending && (
                item.failed ? (
                  <Ionicons name="alert-circle" size={13} color={colors.error} style={styles.messageTick} />
                ) : (
                  <Ionicons
                    name="checkmark-done"
                    size={14}
                    color={item.id === myReadMessageId ? '#4FC3F7' : 'rgba(255,255,255,0.7)'}
                    style={styles.messageTick}
                    accessibilityLabel={item.id === myReadMessageId ? 'Read' : 'Delivered'}
                  />
                )
              )}
            </View>
          )}
        </Pressable>
        {/* Reaction chips — server-backed. Each distinct emoji shows a count;
            mine is highlighted. Tap a chip to toggle my reaction on/off. */}
        {itemReactions.length > 0 && (
          <View style={[styles.reactionChipRow, isMine ? styles.reactionChipRowMine : styles.reactionChipRowTheirs]}>
            {itemReactions.map((r) => (
              <TouchableOpacity
                key={r.emoji}
                activeOpacity={0.8}
                onPress={() => toggleReaction(item.id, r.emoji)}
                style={[
                  styles.reactionChip,
                  r.mine ? styles.reactionChipMine : styles.reactionChipTheirs,
                ]}
                accessibilityRole="button"
                accessibilityLabel={`${r.emoji} ${r.count}${r.mine ? ' — you reacted, tap to remove' : ' — tap to react'}`}
              >
                <Text style={styles.reactionChipEmoji}>{r.emoji}</Text>
                {r.count > 1 && <Text style={styles.reactionChipCount}>{r.count}</Text>}
              </TouchableOpacity>
            ))}
          </View>
        )}
        {/* Failed text send → tap to retry (text preserved on the bubble). */}
        {isMine && item.failed && (
          <TouchableOpacity
            style={styles.retryInline}
            onPress={() => retryText(item)}
            accessibilityRole="button"
            accessibilityLabel="Retry sending message"
          >
            <Ionicons name="alert-circle" size={13} color={colors.error} />
            <Text style={styles.retryInlineText}>Not delivered · Tap to retry</Text>
          </TouchableOpacity>
        )}
        {isMine && !item.failed && item.id === myReadMessageId && (
          <View style={styles.readReceiptRow}>
            <Ionicons name="checkmark-done" size={13} color={colors.muted} />
            <Text style={styles.readReceiptText}>Read</Text>
          </View>
        )}
      </View>
    );
  }

  const otherUser = convo?.other_user;
  const headerTitle = otherUser
    ? otherUser.display_name || otherUser.username
    : 'Messages';

  // Live presence for the peer (online dot / last-seen in the header). Falls back
  // to the REST is_online snapshot until the first socket presence update lands.
  const peerPresence = usePresence(otherUser?.id);
  const peerOnline = peerPresence ? peerPresence.online : !!otherUser?.is_online;
  const peerLastSeenLabel =
    !peerOnline && peerPresence?.lastSeen ? formatLastSeen(peerPresence.lastSeen) : null;

  // Header (skill §12): the native react-navigation bar is hidden and replaced by
  // an in-screen translucent GlassSurface bar (rendered in the body) so the
  // message list scrolls UNDER it — no opaque white strip framing the chat.
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  // In-screen translucent header (skill §7: back-chevron top-left, call actions
  // top-right, name/status centered-left). Renders the EXACT title/status/action
  // JSX that used to live in headerTitle()/headerRight(); all handlers verbatim.
  const renderHeader = () => (
    <GlassSurface
      variant="bar"
      mode="light"
      radius={0}
      sheen={false}
      surfaceColor="transparent"
      style={[styles.header, styles.headerTransparent, { paddingTop: insets.top + spacing.xs }]}
      contentStyle={styles.headerContent}
    >
      <TouchableOpacity
        style={styles.headerBackBtn}
        onPress={() => navigation.goBack()}
        accessibilityRole="button"
        accessibilityLabel="Back"
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <Ionicons name="chevron-back" size={26} color={colors.ink} />
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.headerTitleWrap}
        activeOpacity={0.7}
        disabled={!otherUsername}
        onPress={() => { if (otherUsername) navigation.navigate('UserProfile', { username: otherUsername }); }}
        accessibilityRole="button"
        accessibilityLabel={`View ${headerTitle}'s profile`}
      >
        {otherUser?.avatar_url ? (
          <CachedImage source={{ uri: otherUser.avatar_url }} style={styles.headerAvatar} />
        ) : (
          <View style={[styles.headerAvatar, styles.headerAvatarFallback]}>
            <Text style={styles.headerAvatarLetter}>
              {(headerTitle)[0]?.toUpperCase() || '?'}
            </Text>
          </View>
        )}
        <View style={styles.headerNameCol}>
          <Text style={styles.headerName} numberOfLines={1}>
            {headerTitle}
          </Text>
          {peerTyping ? (
            <Text style={styles.headerOnline}>typing…</Text>
          ) : DM_E2E_ENABLED && sharedKey ? (
            <View style={styles.headerEncRow}>
              <Ionicons name="lock-closed" size={10} color={colors.success} />
              <Text style={styles.headerEnc}>End-to-end encrypted</Text>
            </View>
          ) : peerOnline ? (
            <Text style={styles.headerOnline}>Online</Text>
          ) : peerLastSeenLabel ? (
            <Text style={styles.headerLastSeen}>{peerLastSeenLabel}</Text>
          ) : null}
        </View>
      </TouchableOpacity>
      <View style={styles.headerActions}>
        {/* 📞 Audio call — rings the peer with media:'audio' (videoEnabled:false). */}
        <TouchableOpacity
          style={styles.headerCallBtn}
          onPress={() => handleCall('audio')}
          disabled={!!calling || !otherUser?.id}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Start voice call"
          accessibilityState={{ disabled: !!calling || !otherUser?.id }}
          hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
        >
          {calling === 'audio' ? (
            <ActivityIndicator size="small" color={colors.primary} />
          ) : (
            <Ionicons
              name="call-outline"
              size={22}
              color={otherUser?.id ? colors.primary : colors.faint}
            />
          )}
        </TouchableOpacity>
        {/* 🎥 Video call — rings the peer with media:'video' (videoEnabled:true). */}
        <TouchableOpacity
          style={styles.headerCallBtn}
          onPress={() => handleCall('video')}
          disabled={!!calling || !otherUser?.id}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Start video call"
          accessibilityState={{ disabled: !!calling || !otherUser?.id }}
          hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
        >
          {calling === 'video' ? (
            <ActivityIndicator size="small" color={colors.primary} />
          ) : (
            <Ionicons
              name="videocam-outline"
              size={24}
              color={otherUser?.id ? colors.primary : colors.faint}
            />
          )}
        </TouchableOpacity>
        {/* Trust & Safety (Apple 1.2): report / block the other participant. */}
        <TouchableOpacity
          style={styles.headerCallBtn}
          onPress={() => setTsTarget({
            type: 'user',
            authorId: otherUser?.id,
            authorUsername: otherUser?.username,
            authorLabel: otherUser ? `@${otherUser.username}` : 'this user',
          })}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Conversation options — report or block"
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Ionicons name="ellipsis-horizontal" size={22} color={colors.primary} />
        </TouchableOpacity>
      </View>
    </GlassSurface>
  );

  // Block-filtered, newest-first list (A15). Memoized so the date-separator
  // index math is stable for a given messages/block state.
  const visibleMessages = useMemo(
    () =>
      (Array.isArray(messages) ? messages : []).filter((m) => {
        // Hide pre-gate E2E ciphertext entirely (decision: drop old encrypted
        // messages rather than show "Message unavailable"). With E2E gated off it
        // can never be read here; a text message that's still ciphertext AND has
        // no attachment is dead weight → filter it out. Attachments still render.
        if (!DM_E2E_ENABLED && isEncrypted(m.content) && (m.attachments?.length ?? 0) === 0) {
          return false;
        }
        return m.is_mine || !(isBlocked(m.sender_id, convo?.other_user?.username) || isMuted(m.sender_id, convo?.other_user?.username));
      }),
    [messages, convo?.other_user?.username],
  );

  // Read receipt: the id of MY most-recent message the peer has read. `messages`
  // is newest-first, so we locate the peer's lastReadMessageId then scan from
  // there toward older messages for the first one that's mine — that's the
  // newest of my messages they've seen. Only THIS bubble shows "Read".
  // (Computed BEFORE renderRow so the memoized row renderer can depend on it.)
  let myReadMessageId: string | null = null;
  if (peerLastReadId && messages.length > 0) {
    const idx = messages.findIndex((m) => m.id === peerLastReadId);
    if (idx !== -1) {
      for (let i = idx; i < messages.length; i++) {
        if (messages[i].is_mine) {
          myReadMessageId = messages[i].id;
          break;
        }
      }
    }
  }

  // Stable key (server id) + a memoized row renderer so FlatList doesn't get a
  // fresh function identity every render (keeps rows from thrashing/blanking).
  const keyExtractor = useCallback(
    (item: Message, index: number) => item?.id ?? String(index),
    [],
  );
  const renderRow = useCallback(
    ({ item, index }: { item: Message; index: number }) => {
      // Inverted list: the NEXT (older) item is at index+1. Show a date separator
      // above the FIRST message of each calendar day. NOTE: item cells are
      // auto-counter-flipped by VirtualizedList (each cell gets the same
      // inversion transform as the flipped ScrollView, cancelling out), so row
      // content — bubbles AND this pill — must NOT apply its own scaleY:-1.
      const older = visibleMessages[index + 1];
      const showDay = !older || differentDay(item.created_at, older.created_at);
      // WhatsApp grouping: the NEWER bubble (visually below, index-1 in this
      // inverted list) being same-sender means THIS bubble is mid-run — tighten
      // spacing + drop tail/meta (handled in renderMessage). A day separator
      // below THIS bubble also breaks the run.
      const newer = visibleMessages[index - 1];
      const sameSenderAsNewer =
        !!newer &&
        newer.is_mine === item.is_mine &&
        !differentDay(newer.created_at, item.created_at);
      // Swipe-to-reply: a horizontal Pan that only claims the gesture after a
      // clear rightward drag (activeOffsetX) and bails on vertical movement
      // (failOffsetY) so the list keeps scrolling. Release past threshold → reply.
      // Skipped for optimistic (pending/failed) bubbles — nothing to reply to yet.
      const swipeGesture = Gesture.Pan()
        .enabled(!item.pending && !item.failed)
        .activeOffsetX(20)
        .failOffsetY([-14, 14])
        .onEnd((e) => {
          if (e.translationX > 52) runOnJS(beginReply)(item);
        });
      return (
        <>
          <GestureDetector gesture={swipeGesture}>
            {renderMessage({ item }, { sameSenderAsNewer })}
          </GestureDetector>
          {showDay && !!item.created_at && (
            <View style={styles.daySepWrap}>
              <GlassSurface
                variant="pill"
                radius={glassTokens.radius.pill}
                sheen={false}
                surfaceColor="rgba(255,255,255,0.72)"
                contentStyle={styles.dayPill}
              >
                <Text style={styles.dayPillText}>{formatDay(item.created_at)}</Text>
              </GlassSurface>
            </View>
          )}
        </>
      );
    },
    // renderMessage is re-created each render but renderRow is memoized, so list
    // every value renderMessage closes over that can change WITHOUT a messages
    // update: the E2E key/retry state and the peer read receipt. Otherwise rows
    // would render stale decrypt/read states.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleMessages, sharedKey, retryingIds, myReadMessageId, beginReply],
  );

  return (
    <View style={styles.screen}>
      {/* Full-bleed living particle field (skill §12) — under the status bar and
          all chrome; every sibling chat screen renders this, DM now matches. */}
      <ParticleBackground mesh={false} style={StyleSheet.absoluteFill} />
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
      >
      {/* Translucent in-screen header — the message list scrolls UNDER it. */}
      {renderHeader()}
      {loading ? (
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : error ? (
        <View style={styles.errorWrap}>
          <Asset3D name="error" size={120} style={{ marginBottom: spacing.sm }} />
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity
            style={styles.retryBtn}
            onPress={fetchMessages}
            accessibilityRole="button"
            accessibilityLabel="Retry loading messages"
          >
            <Text style={styles.retryText}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <>
          {/* E2E session-established disclaimer — a centered chip OUTSIDE the
              inverted FlatList so it can never be caught by the list's flip
              transforms (device bug: it rendered upside down as a footer). */}
          {DM_E2E_ENABLED && sharedKey && (
            <View style={[styles.e2eChipWrap, containerStyle]}>
              <GlassSurface
                variant="pill"
                radius={glassTokens.radius.pill}
                sheen={false}
                borderColor="rgba(45,181,93,0.30)"
                surfaceColor="rgba(45,181,93,0.14)"
                contentStyle={styles.e2eChip}
              >
                <Ionicons name="lock-closed" size={11} color={colors.success} />
                <Text style={styles.e2eChipText}>
                  Messages are end-to-end encrypted — not even CRYB can read them
                </Text>
              </GlassSurface>
            </View>
          )}
          <FlatList
            ref={flatListRef}
            data={visibleMessages}
            keyExtractor={keyExtractor}
            renderItem={renderRow}
            // Only invert when there are rows: with no messages the ONLY visible
            // child is ListEmptyComponent, and skipping inversion entirely makes
            // its orientation deterministic on every platform/arch (RN merges the
            // empty component's own style OVER its counter-flip, so an inverted
            // empty state is fragile by construction).
            inverted={visibleMessages.length > 0}
            showsVerticalScrollIndicator={false}
            // Clear the floating glass header: in an inverted list the visual TOP
            // is the content's paddingBottom. ~header height so the first message
            // scrolls under the bar without hiding behind it (skill §12 — the
            // bar's own shadow provides separation, no hard divider).
            contentContainerStyle={[
              styles.messageList,
              { paddingBottom: insets.top + spacing.xxxl + spacing.md },
              containerStyle,
            ]}
            keyboardDismissMode="interactive"
            // Inverted list → onEndReached fires at the TOP (oldest) = load older.
            onEndReached={loadOlder}
            onEndReachedThreshold={0.4}
            removeClippedSubviews
            windowSize={11}
            maxToRenderPerBatch={12}
            ListFooterComponent={
              // In an inverted list the footer renders at the TOP (conversation
              // start). Only the load-older spinner lives here — a spinner is
              // rotation-proof, so no flip transform can garble it. (The E2E
              // banner used to live here and rendered upside down on device;
              // it's now the chip above, outside the list.)
              loadingOlder ? (
                <View style={styles.olderLoading}>
                  <ActivityIndicator size="small" color={colors.primary} />
                </View>
              ) : null
            }
            ListEmptyComponent={
              <View style={styles.emptyWrap}>
                <Asset3D name="chat" size={110} style={{ marginBottom: spacing.sm }} />
                <Text style={styles.emptyText}>No messages yet</Text>
                <Text style={styles.emptySubtext}>Say hello!</Text>
              </View>
            }
          />
        </>
      )}

      {/* Typing indicator — pinned just above the composer. */}
      {peerTyping && !loading && (
        <View style={[styles.typingRow, containerStyle]}>
          <Text style={styles.typingText}>
            {(otherUser?.display_name || otherUser?.username || 'They')} is typing…
          </Text>
        </View>
      )}

      {/* Reply preview — the quoted target shown above the composer while replying
          (swipe-to-reply / menu Reply). Tap the ✕ to cancel. */}
      {replyTarget && (
        <View style={[styles.replyPreview, containerStyle]}>
          <View style={styles.replyPreviewBar} />
          <View style={styles.replyPreviewBody}>
            <Text style={styles.replyPreviewName} numberOfLines={1}>
              {replyTarget.is_mine ? 'You' : (otherUser?.display_name || otherUser?.username || 'Them')}
            </Text>
            <Text style={styles.replyPreviewText} numberOfLines={1}>
              {replyTarget.content?.trim() || (replyTarget.attachments?.length ? 'Attachment' : 'Message')}
            </Text>
          </View>
          <TouchableOpacity
            onPress={() => setReplyTarget(null)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityRole="button"
            accessibilityLabel="Cancel reply"
          >
            <Ionicons name="close-circle" size={22} color={colors.muted} />
          </TouchableOpacity>
        </View>
      )}

      {/* Composer — translucent glass bar (skill §12), not the old white slab. */}
      <GlassSurface
        variant="bar"
        mode="light"
        radius={0}
        sheen={false}
        style={[styles.composerWrap, containerStyle]}
        contentStyle={[styles.inputBar, { paddingBottom: Math.max(insets.bottom, spacing.md) }]}
      >
        {isRecording ? (
          // Recording mode: show elapsed time + cancel/send controls.
          <View style={styles.recordingBar}>
            <TouchableOpacity
              style={styles.recordCancelBtn}
              onPress={() => handleStopRecording(true)}
              accessibilityRole="button"
              accessibilityLabel="Cancel recording"
            >
              <Ionicons name="trash-outline" size={20} color={colors.error} />
            </TouchableOpacity>
            <View style={styles.recordingInfo}>
              <View style={styles.recordingDot} />
              <Text style={styles.recordingTime}>{formatClock(recordSecs)}</Text>
              <Text style={styles.recordingHint}>Recording…</Text>
            </View>
            <TouchableOpacity
              style={styles.sendBtn}
              onPress={() => handleStopRecording(false)}
              accessibilityRole="button"
              accessibilityLabel="Send voice message"
            >
              <Ionicons name="send" size={18} color={colors.white} />
            </TouchableOpacity>
          </View>
        ) : (
          <>
            <TouchableOpacity
              style={styles.iconBtn}
              onPress={handlePickImage}
              disabled={sending}
              accessibilityRole="button"
              accessibilityLabel="Send a photo or video"
            >
              <Ionicons name="image-outline" size={24} color={colors.primary} />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.iconBtn}
              onPress={handlePickDocument}
              disabled={sending}
              accessibilityRole="button"
              accessibilityLabel="Attach a document"
            >
              <Ionicons name="document-attach-outline" size={24} color={colors.primary} />
            </TouchableOpacity>
            <View style={styles.inputWrap}>
              <TextInput
                style={styles.textInput}
                placeholder="Type a message..."
                placeholderTextColor={colors.muted}
                value={inputText}
                onChangeText={handleChangeText}
                multiline
                maxLength={2000}
                returnKeyType="default"
              />
            </View>
            {inputText.trim() ? (
              <TouchableOpacity
                style={[styles.sendBtn, sending && styles.sendBtnDisabled]}
                onPress={handleSend}
                disabled={sending}
                accessibilityRole="button"
                accessibilityLabel="Send message"
              >
                {sending ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <Ionicons name="send" size={18} color={colors.white} />
                )}
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={styles.iconBtn}
                onPress={handleStartRecording}
                accessibilityRole="button"
                accessibilityLabel="Record a voice message"
              >
                <Ionicons name="mic-outline" size={24} color={colors.primary} />
              </TouchableOpacity>
            )}
          </>
        )}
      </GlassSurface>

      {/* Full-screen image viewer */}
      <Modal
        visible={!!viewerUri}
        transparent
        animationType="fade"
        onRequestClose={() => setViewerUri(null)}
      >
        <Pressable style={styles.viewerBackdrop} onPress={() => setViewerUri(null)}>
          <TouchableOpacity
            style={[styles.viewerClose, { top: insets.top + spacing.md }]}
            onPress={() => setViewerUri(null)}
            accessibilityRole="button"
            accessibilityLabel="Close image"
          >
            <Ionicons name="close" size={28} color={colors.white} />
          </TouchableOpacity>
          {viewerUri && (
            <CachedImage source={{ uri: viewerUri }} style={styles.viewerImage} resizeMode="contain" />
          )}
        </Pressable>
      </Modal>
      {/* Long-press message context menu — dims the background + floats a glass
          menu anchored to the pressed bubble (skill §7/§12). Rows are built per
          message (Copy only when there's text; Report their-side; Delete mine). */}
      <ContextMenu
        visible={!!menuTarget}
        onClose={() => setMenuTarget(null)}
        anchor={menuTarget?.anchor ?? null}
        actions={menuTarget ? buildMenuActions(menuTarget.message) : []}
      />
      {/* Long-press emoji REACTION bar — a floating glass pill anchored just above
          the pressed bubble. Tapping an emoji applies MY reaction (optimistic,
          local-only). The trailing "⋯" opens the full context menu. */}
      <Modal
        visible={!!reactionTarget}
        transparent
        animationType="fade"
        onRequestClose={() => setReactionTarget(null)}
      >
        <Pressable style={styles.reactionBackdrop} onPress={() => setReactionTarget(null)}>
          {reactionTarget && (() => {
            const a = reactionTarget.anchor;
            // Prefer sitting ABOVE the bubble; if there's no room (near the top),
            // drop BELOW it. Clamp horizontally into the screen.
            const BAR_H = 52;
            const above = a.y - BAR_H - spacing.sm;
            const top = above > insets.top + spacing.md
              ? above
              : a.y + a.height + spacing.sm;
            const barW = Math.min(320, width - spacing.lg * 2);
            let left = a.x + a.width / 2 - barW / 2;
            left = Math.max(spacing.lg, Math.min(left, width - spacing.lg - barW));
            const positioned = a.width > 0 || a.height > 0;
            return (
              <View
                style={[
                  styles.reactionBarWrap,
                  positioned
                    ? { position: 'absolute', top, left, width: barW }
                    : { alignSelf: 'center', marginTop: '60%' as any },
                ]}
              >
                <GlassSurface
                  variant="pill"
                  mode="light"
                  radius={glassTokens.radius.pill}
                  sheen={false}
                  surfaceColor="rgba(255,255,255,0.72)"
                  borderColor={colors.cardBorder}
                  contentStyle={styles.reactionBar}
                >
                  {REACTION_EMOJIS.map((emoji) => {
                    const active = myReactionFor(reactionTarget.message.id) === emoji;
                    return (
                      <TouchableOpacity
                        key={emoji}
                        activeOpacity={0.7}
                        onPress={() => toggleReaction(reactionTarget.message.id, emoji)}
                        style={[styles.reactionEmojiBtn, active && styles.reactionEmojiBtnActive]}
                        accessibilityRole="button"
                        accessibilityLabel={`React ${emoji}`}
                      >
                        <Text style={styles.reactionEmoji}>{emoji}</Text>
                      </TouchableOpacity>
                    );
                  })}
                  <TouchableOpacity
                    activeOpacity={0.7}
                    onPress={() => {
                      const msg = reactionTarget.message;
                      setReactionTarget(null);
                      setMenuTarget({ message: msg, anchor: reactionTarget.anchor });
                    }}
                    style={styles.reactionMoreBtn}
                    accessibilityRole="button"
                    accessibilityLabel="More message options"
                  >
                    <Ionicons name="ellipsis-horizontal" size={18} color={colors.ink2} />
                  </TouchableOpacity>
                </GlassSurface>
              </View>
            );
          })()}
        </Pressable>
      </Modal>
      <ReportBlockSheet
        visible={!!tsTarget}
        target={tsTarget}
        onClose={() => setTsTarget(null)}
        onBlocked={() => { setTsTarget(null); navigation.goBack(); }}
      />
      {/* Ephemeral status toast (Copy confirmation / not-yet-wired actions). */}
      {toast && (
        <Animated.View
          pointerEvents="none"
          style={[
            styles.toastWrap,
            containerStyle,
            {
              bottom: Math.max(insets.bottom, spacing.md) + spacing.xxxl + spacing.lg,
              opacity: toastAnim,
              transform: [
                { translateY: toastAnim.interpolate({ inputRange: [0, 1], outputRange: [8, 0] }) },
              ],
            },
          ]}
        >
          <GlassSurface
            variant="pill"
            radius={glassTokens.radius.pill}
            sheen={false}
            surfaceColor="rgba(20,20,32,0.86)"
            contentStyle={styles.toastPill}
          >
            <Text style={styles.toastText}>{toast}</Text>
          </GlassSurface>
        </Animated.View>
      )}
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  // Root: transparent so the full-bleed ParticleBackground shows through every
  // layer (header, list, composer are all translucent glass on top).
  screen: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  // In-screen translucent header (skill §12) — replaces the opaque native bar.
  header: {
    // GlassSurface variant="bar" supplies fill + border + shadow (scroll-edge
    // separation, no hard divider). paddingTop is set inline (insets.top).
  },
  // Transparent, seamless header: no fill/border/shadow so the ParticleBackground
  // gradient shows straight through (client: "header same as background, gradiented").
  headerTransparent: {
    borderWidth: 0,
    borderBottomWidth: 0,
    shadowOpacity: 0,
    elevation: 0,
    backgroundColor: 'transparent',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingBottom: spacing.sm,
    gap: spacing.xs,
  },
  headerBackBtn: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerNameCol: {
    flexShrink: 1,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  loadingWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    padding: spacing.xxxl,
  },
  errorText: {
    fontSize: 15,
    color: colors.ink2,
    textAlign: 'center',
    ...fonts.regular,
  },
  retryBtn: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    backgroundColor: colors.primary,
    borderRadius: radius.md,
  },
  retryText: {
    fontSize: 15,
    color: colors.white,
    ...fonts.semibold,
  },
  headerTitleWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  // Narrower than the usual 44 so audio + video + overflow all fit the header
  // next to the title on small phones (tap target padded back out via hitSlop).
  headerCallBtn: {
    width: 36,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  headerAvatarFallback: {
    backgroundColor: colors.primary + '20',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerAvatarLetter: {
    fontSize: 13,
    color: colors.primary,
    ...fonts.bold,
  },
  headerName: {
    fontSize: 16,
    // Was colors.white (dark-mode leftover) → invisible on the light header.
    color: colors.ink,
    ...fonts.semibold,
    maxWidth: 180,
  },
  headerOnline: {
    fontSize: 11,
    color: colors.success,
    ...fonts.medium,
  },
  headerLastSeen: {
    fontSize: 11,
    color: colors.muted,
    ...fonts.medium,
  },
  headerEncRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  headerEnc: {
    fontSize: 11,
    color: colors.success,
    ...fonts.medium,
  },
  olderLoading: {
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  // E2E disclaimer chip — rendered OUTSIDE the inverted list (orientation-proof).
  e2eChipWrap: {
    alignItems: 'center',
    paddingTop: spacing.sm,
    paddingHorizontal: spacing.lg,
  },
  e2eChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: 5,
  },
  e2eChipText: {
    fontSize: 11,
    lineHeight: 14,
    color: 'rgba(255,255,255,0.82)',
    flexShrink: 1,
    ...fonts.medium,
  },
  messageMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-end',
    gap: 3,
    marginTop: 2,
  },
  messageLock: {
    marginBottom: 1,
  },
  messageTick: {
    marginLeft: 1,
    marginBottom: 0,
  },
  // Quoted reply block inside a bubble.
  quoteBlock: {
    flexDirection: 'row',
    gap: spacing.sm,
    borderRadius: radius.sm,
    paddingVertical: 5,
    paddingRight: spacing.sm,
    marginBottom: 5,
    overflow: 'hidden',
  },
  quoteBlockMine: { backgroundColor: 'rgba(255,255,255,0.18)' },
  quoteBlockTheirs: { backgroundColor: 'rgba(108,92,231,0.08)' },
  quoteBar: { width: 3, borderRadius: 2 },
  quoteBarMine: { backgroundColor: 'rgba(255,255,255,0.9)' },
  quoteBarTheirs: { backgroundColor: colors.primary },
  quoteName: { ...fonts.bold, fontSize: 12, color: colors.primary },
  quoteNameMine: { color: 'rgba(255,255,255,0.95)' },
  quoteText: { ...fonts.regular, fontSize: 12, color: colors.ink2 },
  quoteTextMine: { color: 'rgba(255,255,255,0.8)' },
  // Reply preview above the composer.
  replyPreview: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    backgroundColor: 'rgba(108,92,231,0.06)',
  },
  replyPreviewBar: { width: 3, alignSelf: 'stretch', borderRadius: 2, backgroundColor: colors.primary },
  replyPreviewBody: { flex: 1 },
  replyPreviewName: { ...fonts.bold, fontSize: 12, color: colors.primary },
  replyPreviewText: { ...fonts.regular, fontSize: 12, color: colors.ink2 },
  messageList: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
    paddingBottom: spacing.md,
    flexGrow: 1,
  },
  messageBubbleWrap: {
    marginBottom: spacing.sm,
    maxWidth: '80%',
  },
  myMessageWrap: {
    alignSelf: 'flex-end',
  },
  theirMessageWrap: {
    alignSelf: 'flex-start',
  },
  messageBubble: {
    // Tighter WhatsApp-style radius (was radius.lg 20 → too pill-like); the tail
    // (asymmetric small corner below) reads more clearly at this radius.
    borderRadius: radius.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    // Pin layout direction LTR on the bubble container too, so no inherited RTL /
    // list-flip context can propagate into the text below it. There is NO
    // transform (scaleX/scaleY) anywhere on a bubble — VirtualizedList already
    // counter-flips each inverted cell; adding our own would re-mirror content.
    direction: 'ltr',
    // Subtle depth so bubbles lift off the lavender bg (design-system shadow).
    ...shadowTokens.bar,
  },
  myBubble: {
    backgroundColor: colors.primary,
    borderBottomRightRadius: spacing.xs,
  },
  // Their bubble = clean WHITE sheet (WhatsApp on a light chat: theirs = white,
  // mine = colored). The app is light-mode, so the old dark-glass 'their' bubble
  // (a dark-mode leftover) read heavy on the lavender bg. White + hairline border.
  theirBubble: {
    backgroundColor: '#FFFFFF',
    borderBottomLeftRadius: spacing.xs,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  },
  // Grouped (non-last-of-run) bubbles: full radius on ALL corners — no tail — so
  // a consecutive run reads as one block (skill §16.6).
  myBubbleGrouped: {
    borderBottomRightRadius: radius.sm,
  },
  theirBubbleGrouped: {
    borderBottomLeftRadius: radius.sm,
  },
  // Tighter intra-group vertical spacing than the between-sender gap.
  groupedWrap: {
    marginBottom: 2,
  },
  messageText: {
    ...typographyScale.body,
    // CRITICAL: pin text flow left-to-right. Without an explicit writingDirection
    // an inverted FlatList's cell counter-flip could leak into text layout on some
    // platforms/arch (Fabric/Android), rendering message text MIRRORED/backwards.
    // textAlign:'left' + writingDirection:'ltr' force upright, correctly-ordered
    // glyphs regardless of the list flip or any inherited RTL context.
    writingDirection: 'ltr',
    textAlign: 'left',
    ...fonts.regular,
  },
  myMessageText: {
    color: colors.white,
  },
  // Their text sits on the WHITE bubble now → dark ink (WhatsApp-like).
  theirMessageText: {
    color: colors.ink,
  },
  messageTime: {
    fontSize: 10,
    marginTop: spacing.xs,
    alignSelf: 'flex-end',
    ...fonts.regular,
  },
  myMessageTime: {
    // §15 vibrancy: raise contrast over the purple bubble.
    color: 'rgba(255,255,255,0.85)',
  },
  theirMessageTime: {
    color: colors.muted,
  },
  // Failed-send inline retry under MY bubble.
  retryInline: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-end',
    gap: 4,
    marginTop: spacing.xs,
    marginRight: spacing.xs,
  },
  retryInlineText: {
    fontSize: 11,
    color: colors.error,
    ...fonts.medium,
  },
  // Date separator. NO transform here: VirtualizedList already counter-flips
  // every item cell of an inverted list (cell gets the same scaleY:-1 / Android
  // scale:-1 as the flipped ScrollView, cancelling out), so row content renders
  // upright as-is — an extra scaleY:-1 here is what flipped the pills upside
  // down on device.
  daySepWrap: {
    alignItems: 'center',
    marginVertical: spacing.md,
  },
  dayPill: {
    paddingHorizontal: spacing.md,
    paddingVertical: 4,
  },
  dayPillText: {
    fontSize: 11,
    // Was white (dark-mode leftover) → invisible on the light pill. Dark now.
    color: colors.ink2,
    ...fonts.semibold,
  },
  // Read receipt under MY latest read bubble.
  readReceiptRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-end',
    gap: 3,
    marginTop: spacing.xs,
    marginRight: spacing.xs,
  },
  readReceiptText: {
    fontSize: 10,
    color: colors.muted,
    ...fonts.medium,
  },
  // Typing indicator pinned above the composer.
  typingRow: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xs,
  },
  typingText: {
    fontSize: 12,
    color: colors.muted,
    fontStyle: 'italic',
    ...fonts.regular,
  },
  // Composer bar is a GlassSurface variant="bar" (fill + border + shadow supplied
  // by the material). composerWrap = outer (radius 0, full-bleed).
  composerWrap: {
    borderRadius: 0,
  },
  inputBar: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    gap: spacing.sm,
  },
  // Clean WHITE rounded input pill (WhatsApp/iMessage). The old dark translucent
  // pill + white text were dark-mode leftovers → invisible on the light bar.
  inputWrap: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: radius.lg,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    maxHeight: 100,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  },
  textInput: {
    fontSize: 15,
    color: colors.ink,
    padding: 0,
    maxHeight: 80,
    ...fonts.regular,
  },
  sendBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.xs,
  },
  sendBtnDisabled: {
    opacity: 0.4,
  },
  iconBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.xs,
  },
  // Attachment rendering
  attachmentBlock: {
    marginBottom: spacing.xs,
  },
  imageAttachment: {
    borderRadius: radius.md,
    backgroundColor: 'rgba(0,0,0,0.06)',
  },
  uploadOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
  },
  uploadOverlayText: {
    color: colors.white,
    fontSize: 12,
    ...fonts.medium,
  },
  audioWrap: {
    minWidth: 180,
  },
  // 🔒 encrypted-media placeholder (resolving spinner or decrypt-failed state).
  encryptedPlaceholder: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minWidth: 160,
    minHeight: 56,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.md,
  },
  encryptedPlaceholderText: {
    fontSize: 13,
    ...fonts.medium,
  },
  encryptedPlaceholderHint: {
    fontSize: 11,
    marginTop: 1,
    ...fonts.regular,
  },
  // "Couldn't decrypt — tap to retry" bubble content (E2E failed state).
  decryptFailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 2,
  },
  decryptRetryHint: {
    fontSize: 11,
    marginTop: 1,
    ...fonts.medium,
  },
  audioUploading: {
    fontSize: 11,
    marginTop: spacing.xs,
    ...fonts.regular,
  },
  fileChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
    maxWidth: 220,
  },
  fileChipText: {
    fontSize: 14,
    flexShrink: 1,
    ...fonts.medium,
  },
  // Recording bar
  recordingBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  recordCancelBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  recordingInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: 'rgba(255,255,255,0.14)',
    borderRadius: radius.lg,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
  },
  recordingDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.error,
  },
  recordingTime: {
    fontSize: 15,
    color: colors.white,
    ...fonts.semibold,
    // Live-ticking m:ss — tabular figures so digits don't reflow as it counts.
    ...tabularNums,
  },
  recordingHint: {
    fontSize: 13,
    color: colors.muted,
    ...fonts.regular,
  },
  // Full-screen viewer
  viewerBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.95)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewerImage: {
    width: '100%',
    height: '100%',
  },
  viewerClose: {
    position: 'absolute',
    right: spacing.lg,
    zIndex: 2,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Empty state. NO transform: the list is only inverted when it has rows (see
  // the `inverted` prop), so this renders in a plain, non-flipped list. The old
  // scaleY:-1 here OVERRODE RN's own counter-flip on ListEmptyComponent (styles
  // are merged with the app style last), which mirrored the text on Android.
  emptyWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 60,
    gap: spacing.sm,
  },
  emptyText: {
    fontSize: 16,
    color: colors.white,
    ...fonts.semibold,
  },
  emptySubtext: {
    fontSize: 13,
    color: colors.muted,
    ...fonts.regular,
  },
  // Ephemeral status toast (Copy / not-yet-wired actions) — a small dark glass
  // pill centered above the composer.
  toastWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  toastPill: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  toastText: {
    ...fonts.semibold,
    fontSize: 13,
    color: 'rgba(255,255,255,0.95)',
    textAlign: 'center',
  },
  // --- Reactions ---
  // The applied-reaction chip that sits under a bubble.
  // Row of reaction chips sitting just under a bubble.
  reactionChipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: -6,
  },
  reactionChipRowMine: {
    justifyContent: 'flex-end',
    marginRight: spacing.xs,
  },
  reactionChipRowTheirs: {
    justifyContent: 'flex-start',
    marginLeft: spacing.xs,
  },
  reactionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: radius.full,
    backgroundColor: 'rgba(24,24,38,0.9)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  // Mine — brand-tinted so my reaction is visually distinct.
  reactionChipMine: {
    backgroundColor: 'rgba(108,92,231,0.95)',
    borderColor: 'rgba(255,255,255,0.55)',
  },
  reactionChipTheirs: {
    backgroundColor: 'rgba(24,24,38,0.9)',
    borderColor: 'rgba(255,255,255,0.18)',
  },
  reactionChipEmoji: {
    fontSize: 14,
  },
  reactionChipCount: {
    fontSize: 11,
    color: '#FFFFFF',
    ...fonts.semibold,
  },
  // Long-press reaction bar overlay.
  reactionBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.25)',
  },
  reactionBarWrap: {
    // Positioned inline (absolute top/left) when a bubble frame was measured.
  },
  reactionBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingHorizontal: spacing.xs,
    paddingVertical: 4,
  },
  reactionEmojiBtn: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 20,
  },
  reactionEmojiBtnActive: {
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  reactionEmoji: {
    fontSize: 24,
  },
  reactionMoreBtn: {
    width: 36,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 18,
    marginLeft: 2,
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(255,255,255,0.14)',
  },
});
